import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth, VIEWING_AS_NOTE } from '../context/AuthContext';
import { fetchList } from '../lib/listQuery';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import GuidedTour from '../components/tour/GuidedTour';
import BulkAddFixtures from '../components/BulkAddFixtures';
import FixtureFinder from '../components/fixtureFinder/FixtureFinder';
import SeasonTraining from '../components/SeasonTraining';
import SetupChecklist from '../components/SetupChecklist';
import AttendanceRegister, { eventHasStarted } from '../components/attendance/AttendanceRegister';
import { toIsoUtc } from '../lib/datetime';
import ShareMenu from '../components/ShareMenu';
import SocialLinkCards from '../components/SocialLinkCards';
import { bodyWithoutCardLinks } from '../lib/socialLinks';
import { homeGroundLabel } from '../lib/venue';
import { eventShareText, eventShareUrl, newsShareText, newsShareUrl } from '../lib/share';
import { kindLabel, groupByDay } from '../lib/whatsOn';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { Pin, PinOff, Megaphone, Calendar, Trophy, Users, Plus, Trash2, Check, X, MessageCircle, Send, Filter, Dumbbell, ChevronDown, Navigation, MoreVertical, Pencil, Flag, Gift, Home, Upload, TrendingUp, MapPin, Hourglass, LogIn, UserPlus, CalendarDays, ChevronRight } from 'lucide-react';
import './ClubHub.css';

const categoryColors = { general: '#4A7C59', training: '#2D7A4F', match: '#C4943A', event: '#7C6A9E', urgent: '#B94040' };
const eventTypeIcons = { match: '🏉', training: '🏃', social: '🎉', general: '📅' };
// What's on kind labels: fixed brand badges with readable contrast, never the
// club colour (F-008).
const KIND_BADGE = { Fixture: 'badge-gold', Training: 'badge-green', Event: 'badge-silver' };

// ─── Weather forecast — Open-Meteo (no API key required) ───

// WMO weather codes → icon + short label. https://open-meteo.com/en/docs (Weather variable documentation)
const weatherCodeMap = (code) => {
  if (code === 0)             return { icon: '☀️', label: 'Clear' };
  if (code === 1)             return { icon: '🌤️', label: 'Mostly sunny' };
  if (code === 2)             return { icon: '⛅', label: 'Partly cloudy' };
  if (code === 3)             return { icon: '☁️', label: 'Overcast' };
  if (code === 45 || code === 48) return { icon: '🌫️', label: 'Fog' };
  if (code >= 51 && code <= 57)   return { icon: '🌦️', label: 'Drizzle' };
  if (code >= 61 && code <= 65)   return { icon: '🌧️', label: 'Rain' };
  if (code === 66 || code === 67) return { icon: '🌧️', label: 'Freezing rain' };
  if (code >= 71 && code <= 77)   return { icon: '❄️', label: 'Snow' };
  if (code >= 80 && code <= 82)   return { icon: '🌦️', label: 'Showers' };
  if (code === 85 || code === 86) return { icon: '🌨️', label: 'Snow showers' };
  if (code >= 95)                 return { icon: '⛈️', label: 'Thunderstorm' };
  return { icon: '🌡️', label: 'Forecast' };
};

// Friendly kit hint based on conditions — quick read for parents prepping kids
const kitHint = ({ tempC, precipPct, windKph, weatherCode }) => {
  const hints = [];
  if (tempC != null && tempC <= 4) hints.push('gloves & warm layers');
  else if (tempC != null && tempC <= 10) hints.push('extra layer');
  if (precipPct != null && precipPct >= 50) hints.push('waterproof');
  else if (precipPct != null && precipPct >= 30) hints.push('rain jacket just in case');
  if (windKph != null && windKph >= 30) hints.push('windproof');
  if (tempC != null && tempC <= 6) hints.push('hot drink');
  if (tempC != null && tempC >= 24) hints.push('sun cream & water');
  return hints.length ? hints.join(' · ') : null;
};

// Fetches a forecast for a venue and returns the hour closest to startsAt.
// One query per (lat,lng) per day; TanStack Query handles caching/dedup.
const useWeatherForecast = (lat, lng, startsAt) => {
  const startDate = startsAt ? new Date(startsAt) : null;
  const now = new Date();
  const daysOut = startDate ? Math.round((startDate - now) / (1000 * 60 * 60 * 24)) : null;
  const inRange = daysOut != null && daysOut >= 0 && daysOut <= 14;
  const hasCoords = lat != null && lng != null;

  return useQuery({
    // Round to 3dp so events at the same ground share one cache key
    queryKey: ['weather', Number(lat).toFixed(3), Number(lng).toFixed(3)],
    queryFn: async () => {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&hourly=temperature_2m,apparent_temperature,precipitation_probability,weather_code,wind_speed_10m&forecast_days=16&timezone=auto&wind_speed_unit=kmh`;
      const r = await fetch(url);
      if (!r.ok) throw new Error('Weather fetch failed');
      return r.json();
    },
    enabled: hasCoords && inRange,
    staleTime: 30 * 60 * 1000, // 30 min — forecasts don't change that fast
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// Pick the forecast hour closest to the event start time.
const pickForecastForTime = (data, startsAt) => {
  if (!data?.hourly?.time?.length || !startsAt) return null;
  const target = new Date(startsAt).getTime();
  const { time, temperature_2m, apparent_temperature, precipitation_probability, weather_code, wind_speed_10m } = data.hourly;
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < time.length; i++) {
    const diff = Math.abs(new Date(time[i]).getTime() - target);
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
  }
  // If the closest hour is more than 2 hours from target, the forecast doesn't cover this date
  if (bestDiff > 2 * 60 * 60 * 1000) return null;
  return {
    tempC: temperature_2m?.[bestIdx],
    feelsC: apparent_temperature?.[bestIdx],
    precipPct: precipitation_probability?.[bestIdx],
    windKph: wind_speed_10m?.[bestIdx],
    weatherCode: weather_code?.[bestIdx],
  };
};

// Compact pill for collapsed event header — icon + temp
const WeatherPill = ({ lat, lng, startsAt }) => {
  const { data, isLoading, isError } = useWeatherForecast(lat, lng, startsAt);
  if (isLoading || isError || !data) return null;
  const f = pickForecastForTime(data, startsAt);
  if (!f || f.tempC == null) return null;
  const { icon } = weatherCodeMap(f.weatherCode);
  return (
    <span className="weather-pill" title="Forecast at kick-off">
      <span className="weather-pill-icon">{icon}</span>
      <span className="weather-pill-temp">{Math.round(f.tempC)}°</span>
    </span>
  );
};

// Full forecast block for expanded panel
const WeatherDetail = ({ lat, lng, startsAt }) => {
  const { data, isLoading, isError } = useWeatherForecast(lat, lng, startsAt);
  if (lat == null || lng == null) return null;

  const startDate = new Date(startsAt);
  const daysOut = Math.round((startDate - new Date()) / (1000 * 60 * 60 * 24));
  if (daysOut < 0) return null; // past event
  // Hide the whole section unless there's real forecast data to show (i.e.
  // the event is within the ~14-day forecast window). No placeholder.
  if (daysOut > 14) return null;
  if (isLoading || isError) return null;
  const f = pickForecastForTime(data, startsAt);
  if (!f || f.tempC == null) return null;

  const { icon, label } = weatherCodeMap(f.weatherCode);
  const hint = kitHint(f);
  // Open-Meteo's confidence window is roughly 14 days for grassroots needs
  // (we just want a "bring waterproofs?" hint, not a precision forecast).
  // We hide entirely beyond 14 days above, so anything that gets here is fine.
  const reliable = daysOut <= 14;

  return (
    <div className="weather-detail">
      <div className="weather-detail-main">
        <span className="weather-detail-icon">{icon}</span>
        <div className="weather-detail-body">
          <div className="weather-detail-row">
            <strong>{Math.round(f.tempC)}°C</strong>
            <span className="weather-detail-label">{label}</span>
            {!reliable && <span className="weather-detail-est">(estimate)</span>}
          </div>
          <div className="weather-detail-meta">
            {f.feelsC != null && <span>Feels {Math.round(f.feelsC)}°</span>}
            {f.precipPct != null && <span>💧 {f.precipPct}% rain</span>}
            {f.windKph != null && <span>💨 {Math.round(f.windKph)} kph</span>}
          </div>
        </div>
      </div>
      {hint && <div className="weather-detail-hint">👶 Bring: {hint}</div>}
    </div>
  );
};

// Typeahead for opponent clubs — queries the shared opponent_clubs directory.
// Auto-fills the event title + venue based on home/away.
const OpponentTypeahead = ({ value, onSelect, onFreeText, clubSlug, placeholder = 'e.g. Clevedon RFC' }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = React.useRef();

  // Sync external value changes (e.g. when editing existing event)
  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    if (!debounceRef.current) debounceRef.current = {};
    clearTimeout(debounceRef.current.timer);
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current.timer = setTimeout(() => {
      axios.get(`/clubs/${clubSlug}/opponents`, { params: { q: query.trim() } })
        .then(r => setSuggestions(r.data))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(debounceRef.current.timer);
  }, [query, clubSlug]);

  const handleSelect = (opp) => {
    setQuery(opp.name);
    setShowList(false);
    setHighlight(-1);
    onSelect(opp);
  };

  const handleChange = (val) => {
    setQuery(val);
    setShowList(true);
    setHighlight(-1);
    if (onFreeText) onFreeText(val);
  };

  const handleKey = (e) => {
    if (!showList || !suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); handleSelect(suggestions[highlight]); }
    else if (e.key === 'Escape') { setShowList(false); setHighlight(-1); }
  };

  return (
    <div className="opponent-typeahead">
      <input
        className="input"
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 150)}
        onKeyDown={handleKey}
        autoComplete="off"
      />
      {showList && suggestions.length > 0 && (
        <ul className="typeahead-list">
          {suggestions.map((opp, idx) => (
            <li
              key={opp.id}
              className={`typeahead-item ${idx === highlight ? 'active' : ''}`}
              onMouseDown={() => handleSelect(opp)}
            >
              <span className="typeahead-name">{opp.name}</span>
              {opp.ground_name && <span className="typeahead-ground">{opp.ground_name}</span>}
            </li>
          ))}
        </ul>
      )}
      {showList && query.trim().length >= 2 && suggestions.length === 0 && (
        <div className="typeahead-empty">No matches — they'll be saved as a new club</div>
      )}
    </div>
  );
};

// Re-derives the venue (location text + lat/lng) from the form's selectedOpponent
// and isHome. Pure function — does not mutate the input. Called from BOTH the
// opponent select handler AND the isHome change handler so the order in which
// the user fills the form doesn't matter — venue auto-fills once both are known.
//
// Respects locationDirty: if the admin has manually typed in the venue, we
// don't overwrite their input.
const applyVenueAutofill = (state, club) => {
  if (state.locationDirty) return state;
  const next = { ...state };
  if (state.isHome === false && state.selectedOpponent) {
    const opp = state.selectedOpponent;
    next.location = opp.ground_name
      ? `${opp.name} — ${opp.ground_name}`
      : (opp.address || opp.name);
    next.venueLat = opp.latitude != null ? Number(opp.latitude) : null;
    next.venueLng = opp.longitude != null ? Number(opp.longitude) : null;
  } else if (state.isHome === true && club?.home_ground_name) {
    next.location = club.home_ground_name;
    next.venueLat = club.home_ground_lat != null ? Number(club.home_ground_lat) : null;
    next.venueLng = club.home_ground_lng != null ? Number(club.home_ground_lng) : null;
  }
  return next;
};

// WT-28: "1 player", "2 players" (was "1 players").
const plural = (n, one, many = `${one}s`) => `${n} ${Number(n) === 1 ? one : many}`;

// B-81: a player limit is a whole number 1–999 (the input's own min/max).
// Anything else with the limit switched on blocks the save (capProblem in
// ClubHub keeps an existing larger cap as it is).
const isValidPlayerCap = (v) => {
  const s = String(v ?? '').trim();
  if (!/^\d+$/.test(s)) return false;
  const n = parseInt(s, 10);
  return n >= 1 && n <= 999;
};

// v31 — 15-minute time chunks (Stuart, 24 Aug). Native pickers can't be
// trusted here: Chrome ignores `step` on datetime-local entirely, and newer
// Chrome ignores it in the time-input dropdown too. So times are a plain
// <select> of quarter-hours — nothing to ignore. A stored off-grid time
// (legacy minute-granular data) is kept as an extra option so editing an old
// event doesn't silently shift it.
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTE_OPTIONS = ['00', '15', '30', '45'];

// Separate hour + minute dropdowns (Stuart, 24 Aug): hour 00–23, minutes just
// 00/15/30/45. Picking an hour defaults minutes to 00 so one click gives a
// full time; hour "--" clears. An off-grid stored minute (legacy data) is
// kept as an extra option so editing an old event doesn't silently shift it.
const TimeSelect = ({ value, onChange, disabled }) => {
  const [h, m] = (value || '').split(':');
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <select className="input" style={{ width: 72 }} disabled={disabled} value={h || ''}
        onChange={e => onChange(e.target.value === '' ? '' : `${e.target.value}:${m || '00'}`)}>
        <option value="">--</option>
        {HOUR_OPTIONS.map(hh => <option key={hh} value={hh}>{hh}</option>)}
      </select>
      <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>:</span>
      <select className="input" style={{ width: 72 }} disabled={disabled || !h} value={m || '00'}
        onChange={e => onChange(`${h}:${e.target.value}`)}>
        {m && !MINUTE_OPTIONS.includes(m) && <option value={m}>{m}</option>}
        {MINUTE_OPTIONS.map(mm => <option key={mm} value={mm}>{mm}</option>)}
      </select>
    </div>
  );
};

// Split date + quarter-hour time pair. Value in/out stays the same
// "YYYY-MM-DDTHH:MM" local string the forms already use; emits '' while the
// pair is incomplete. Local state means a half-filled pair isn't wiped when
// the parent re-renders with the still-empty combined value.
const DateTimeField = ({ value, onChange }) => {
  const [d, setD] = React.useState(() => (value || '').split('T')[0] || '');
  const [t, setT] = React.useState(() => (value || '').split('T')[1] || '');
  React.useEffect(() => {
    const composed = d && t ? `${d}T${t}` : '';
    if ((value || '') !== composed) {
      const [nd, nt] = (value || '').split('T');
      setD(nd || '');
      setT(nt || '');
    }
  }, [value]);
  const emit = (nd, nt) => onChange(nd && nt ? `${nd}T${nt}` : '');
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input className="input" type="date" value={d}
        onChange={e => { setD(e.target.value); emit(e.target.value, t); }} />
      <TimeSelect value={t} onChange={v => { setT(v); emit(d, v); }} />
    </div>
  );
};

// Which hub section lists an event type (mirrors the per-type queries).
const sectionForEventType = (type) => (type === 'match' ? 'fixtures' : type === 'training' ? 'training' : 'events');

const formatEventDate = (dateStr) => {
  const d = parseISO(dateStr);
  if (isToday(d)) return `Today · ${format(d, 'HH:mm')}`;
  if (isTomorrow(d)) return `Tomorrow · ${format(d, 'HH:mm')}`;
  return format(d, 'EEE d MMM · HH:mm');
};

// Date label only (no time) — used when a meet time is shown so the card can
// read "Sat 6 Sep · Meet 10:00 · KO 10:30" with meet FIRST (Stuart, 24 Aug).
const formatEventDateLabel = (dateStr) => {
  const d = parseISO(dateStr);
  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tomorrow';
  return format(d, 'EEE d MMM');
};

// ─── User location store + drive-time hook ────────────────────────
// Lightweight module-level singleton so every EventCard sees the same
// location once a parent has granted permission — no need to re-prompt
// per card. Subscribers re-render on any state change.
//
// Privacy: the user's lat/lng never touches the server. OSRM is called
// directly from the browser, and the location is held in memory only —
// it's lost on tab close.
const LOC_CACHE_KEY = 'marakin_last_loc';
const LOC_CACHE_MAX_AGE = 30 * 60 * 1000; // 30 min — plenty for journey-time purposes

// Get a position from the right source: Capacitor Geolocation in the native
// app (proper Android/iOS permission flow), navigator.geolocation on web.
// Resolves { lat, lng } or rejects { code, message } (code 1 = denied).
const getDevicePosition = async ({ prompt }) => {
  const opts = { timeout: 10000, maximumAge: 10 * 60 * 1000, enableHighAccuracy: false };
  if (Capacitor.isNativePlatform()) {
    let perm = await Geolocation.checkPermissions();
    let state = perm.location === 'granted' || perm.coarseLocation === 'granted' ? 'granted' : perm.location;
    if (state !== 'granted') {
      if (!prompt || state === 'denied') throw { code: 1, message: 'Location permission ' + state };
      perm = await Geolocation.requestPermissions({ permissions: ['location'] });
      state = perm.location === 'granted' || perm.coarseLocation === 'granted' ? 'granted' : perm.location;
      if (state !== 'granted') throw { code: 1, message: 'Location permission ' + state };
    }
    const pos = await Geolocation.getCurrentPosition(opts);
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  }
  if (!navigator.geolocation) throw { code: 0, message: 'unsupported' };
  if (!prompt) {
    // Don't surprise web users with a permission popup — only fetch silently
    // if the browser says permission is already granted.
    if (!navigator.permissions?.query) throw { code: 1, message: 'cannot check silently' };
    const st = await navigator.permissions.query({ name: 'geolocation' });
    if (st.state !== 'granted') throw { code: 1, message: 'not yet granted' };
  }
  const pos = await new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, opts));
  return { lat: pos.coords.latitude, lng: pos.coords.longitude };
};

const readLocCache = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(LOC_CACHE_KEY));
    if (raw && Date.now() - raw.ts < LOC_CACHE_MAX_AGE &&
        Number.isFinite(raw.lat) && Number.isFinite(raw.lng)) return raw;
  } catch { /* ignore */ }
  return null;
};

const userLocStore = {
  loc: null,            // { lat, lng }
  status: 'idle',       // 'idle' | 'pending' | 'granted' | 'denied' | 'unsupported' | 'error'
  error: null,
  inflight: false,
  initDone: false,
  subs: new Set(),
  notify() { this.subs.forEach(fn => fn()); },
  _apply(loc) {
    this.loc = loc;
    this.status = 'granted';
    this.error = null;
    try { localStorage.setItem(LOC_CACHE_KEY, JSON.stringify({ ...loc, ts: Date.now() })); } catch { /* ignore */ }
    this.notify();
  },
  _fail(err, { silent }) {
    // On a silent init, failure just means "stay idle and wait for the
    // opt-in button" — don't show 'denied' for a user who was never asked.
    if (silent && this.loc == null) { this.status = 'idle'; }
    else if (!silent) { this.status = err?.code === 1 ? 'denied' : (err?.code === 0 ? 'unsupported' : 'error'); }
    this.error = err?.message || String(err);
    this.notify();
  },
  // Instant leave-by (UI tweak #6): hydrate from the device-local cache so
  // journey times render immediately on open, then refresh in the background
  // WITHOUT prompting (only fetches if permission is already granted).
  // The location only ever lives on the device + our /directions proxy params.
  init() {
    if (this.initDone) return;
    this.initDone = true;
    const cached = readLocCache();
    if (cached) {
      this.loc = { lat: cached.lat, lng: cached.lng };
      this.status = 'granted';
      this.notify();
    }
    (async () => {
      try { this._apply(await getDevicePosition({ prompt: false })); }
      catch (err) { this._fail(err, { silent: true }); }
    })();
  },
  // Explicit user opt-in ("Use my location") — may show the OS prompt.
  request() {
    if (this.inflight) return;
    this.inflight = true;
    this.status = 'pending';
    this.notify();
    (async () => {
      try { this._apply(await getDevicePosition({ prompt: true })); }
      catch (err) { this._fail(err, { silent: false }); }
      finally { this.inflight = false; this.notify(); }
    })();
  },
};

const useUserLocation = () => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick(t => t + 1);
    userLocStore.subs.add(fn);
    userLocStore.init(); // hydrate from cache + silent refresh (no prompt)
    return () => userLocStore.subs.delete(fn);
  }, []);
  return {
    loc: userLocStore.loc,
    status: userLocStore.status,
    error: userLocStore.error,
    request: () => userLocStore.request(),
  };
};

// OSRM public demo — free, no API key. Light usage only (we're a pilot
// with a few parents at any one time; well within their fair-use guidance).
// If we ever need to scale this up, swap in either a self-hosted OSRM or
// OpenRouteService with an API key. CORS-friendly out of the box.
// Multi-modal journey times via the backend Mapbox proxy (car/bike/walk).
// Returns { car|bike|walk: { durationSec, distanceM } } for the requested modes.
const useJourneyTimes = (from, to, modes) => {
  const enabled = !!(from?.lat && from?.lng && to?.lat != null && to?.lng != null);
  const modeStr = (modes && modes.length ? modes : ['car', 'bike', 'walk']).join(',');
  return useQuery({
    // Round user coords to 3dp (~110m) so small drifts don't bust the cache
    queryKey: ['journey',
      from ? `${Number(from.lat).toFixed(3)},${Number(from.lng).toFixed(3)}` : null,
      to   ? `${Number(to.lat).toFixed(4)},${Number(to.lng).toFixed(4)}` : null,
      modeStr,
    ],
    queryFn: () => axios.get('/directions', { params: {
      fromLat: from.lat, fromLng: from.lng, toLat: to.lat, toLng: to.lng, modes: modeStr,
    } }).then(r => r.data),
    enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

// Format a duration in seconds as "12m" / "1h 5m".
const fmtJourney = (sec) => {
  const m = Math.max(1, Math.round(sec / 60));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

// Compact "Leave by" pill for the collapsed card — only shown when the
// user has granted location AND the drive is under ~2 hours (anything
// longer is almost certainly "parent is travelling for work" and isn't a
// useful leave-by time).
const LeaveByPill = ({ startsAt, venueLat, venueLng }) => {
  const { loc, status } = useUserLocation();
  const lat = venueLat != null ? Number(venueLat) : null;
  const lng = venueLng != null ? Number(venueLng) : null;
  const venueOk = lat != null && !Number.isNaN(lat) && lng != null && !Number.isNaN(lng);
  const { data } = useJourneyTimes(status === 'granted' ? loc : null, venueOk ? { lat, lng } : null, ['car']);
  const car = data?.car;

  if (status !== 'granted' || !car || !venueOk) return null;
  if (car.durationSec > 2 * 60 * 60) return null; // >2h drive — probably traveling
  const eventTime = new Date(startsAt).getTime();
  const leaveBy = new Date(eventTime - car.durationSec * 1000);
  if (leaveBy.getTime() < Date.now() - 5 * 60 * 1000) return null; // already past the leave-by time
  return (
    <span style={{ fontSize: 14 }} title={`About ${fmtJourney(car.durationSec)} drive`}>
      🚗 Leave {format(leaveBy, 'HH:mm')}
    </span>
  );
};

// Full leave-by detail for the expanded panel — includes the opt-in
// "Show leave-by time" button if location hasn't been granted yet.
const LeaveByDetail = ({ startsAt, venueLat, venueLng }) => {
  const { loc, status, request } = useUserLocation();
  const [mode, setMode] = useState('car');
  const lat = venueLat != null ? Number(venueLat) : null;
  const lng = venueLng != null ? Number(venueLng) : null;
  const venueOk = lat != null && !Number.isNaN(lat) && lng != null && !Number.isNaN(lng);
  const { data, isLoading, isError } = useJourneyTimes(status === 'granted' ? loc : null, venueOk ? { lat, lng } : null);

  if (!venueOk) return null;

  // Common wrapper
  const Card = ({ children }) => (
    <div style={{
      padding: '10px 12px',
      background: 'var(--surface-2, #F4EFE6)',
      borderRadius: 8,
      marginBottom: 12,
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
    }}>{children}</div>
  );

  if (status === 'idle') {
    return (
      <Card>
        <span style={{ fontSize: 16 }}>🚗</span>
        <span style={{ color: 'var(--text-secondary)' }}>Want to know when to leave?</span>
        <button
          className="btn btn-sm"
          style={{ background: 'var(--marakin-accent, #1E4A35)', color: '#fff', padding: '4px 10px' }}
          onClick={(e) => { e.stopPropagation(); request(); }}
        >
          Use my location
        </button>
      </Card>
    );
  }
  if (status === 'pending' || isLoading) {
    return <Card><span>🚗</span><span style={{ color: 'var(--text-secondary)' }}>Working out journey times…</span></Card>;
  }
  if (status === 'denied') {
    return (
      <Card>
        <span>🚗</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          {Capacitor.isNativePlatform()
            ? 'Location is off for Marakin. Allow location in your phone’s Settings → Apps → Marakin to see leave-by times.'
            : 'Location blocked. Enable location in your browser to see leave-by time.'}
        </span>
      </Card>
    );
  }
  if (status === 'unsupported' || status === 'error' || isError) {
    return null; // fail quietly — map + weather are still useful
  }
  if (!data) return null;

  const MODES = [
    { key: 'car', icon: '🚗', verb: 'drive', d: data.car },
    { key: 'bike', icon: '🚲', verb: 'cycle', d: data.bike },
    { key: 'walk', icon: '🚶', verb: 'walk', d: data.walk },
  ].filter(m => m.d && m.d.durationSec != null);
  if (!MODES.length) return null;

  const sel = MODES.find(m => m.key === mode) || MODES[0];
  const eventTime = new Date(startsAt).getTime();
  const leaveBy = new Date(eventTime - sel.d.durationSec * 1000);
  const distMi = (sel.d.distanceM / 1609.34).toFixed(1);
  const isPastLeave = leaveBy.getTime() < Date.now() - 5 * 60 * 1000;

  return (
    <div style={{ padding: '10px 12px', background: 'var(--surface-2, #F4EFE6)', borderRadius: 8, marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={(e) => { e.stopPropagation(); setMode(m.key); }}
            title={`${fmtJourney(m.d.durationSec)} by ${m.verb}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 100,
              fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${m.key === sel.key ? 'var(--marakin-accent, #1E4A35)' : 'var(--border)'}`,
              background: m.key === sel.key ? 'rgba(30,74,53,0.12)' : 'transparent',
              color: 'var(--text-primary)', fontWeight: m.key === sel.key ? 600 : 400,
            }}
          >
            <span style={{ fontSize: 15 }}>{m.icon}</span> {fmtJourney(m.d.durationSec)}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 14 }}>
        {isPastLeave ? (
          <span style={{ color: '#B94040' }}>
            Should have left at <strong>{format(leaveBy, 'HH:mm')}</strong>
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {fmtJourney(sel.d.durationSec)} {sel.verb} · {distMi} mi</span>
          </span>
        ) : (
          <span>
            Leave by <strong>{format(leaveBy, 'HH:mm')}</strong>
            <span style={{ color: 'var(--text-secondary)' }}> · {fmtJourney(sel.d.durationSec)} {sel.verb} · {distMi} mi from you</span>
          </span>
        )}
      </div>
    </div>
  );
};

const EventCard = ({ ev, fixtureView, accentColor, user, openChatEventId, setOpenChatEventId, chatMessages, chatOlderCount = 0, chatMessage, setChatMessage, rsvpMutation, sendMessageMutation, expandedEventId, setExpandedEventId, rsvpAttendees, canChaseRsvp, canManage, onEditEvent, onDeleteEvent, isNextUpcoming, myChildren, membership, remindMutation, showKind = false }) => {
  const isExpanded = expandedEventId === ev.id;
  const { clubSlug: cardClubSlug } = useParams();

  // v31/B-30 scoping: admins manage every entry; TMs/coaches only THIS
  // event's team (a player hat on another team grants nothing). Club-wide
  // entries: admins + team managers. Legacy single-role members (no roster
  // rows) keep club-role behaviour. Mirrors canManageEventTeam server-side.
  const canManageThisEvent = (() => {
    if (!canManage) return false;
    if (membership?.clubAdmin || membership?.role === 'admin') return true;
    const roster = membership?.teamRoles || [];
    const legacy = roster.length === 0;
    if (!ev.team_id) {
      return legacy
        ? membership?.role === 'team_manager'
        : roster.some(tr => tr.role === 'team_manager') || membership?.role === 'team_manager';
    }
    if (legacy) return ['team_manager', 'coach'].includes(membership?.role);
    const mine = roster.find(tr => String(tr.teamId) === String(ev.team_id));
    return !!mine && ['team_manager', 'coach'].includes(mine.role);
  })();

  // UGC report flag (Google Play UGC policy): flag a chat message to club
  // admins. Local set so the flag flips to "Reported ✓" immediately.
  const [reportedMsgIds, setReportedMsgIds] = useState(new Set());
  const reportMessage = async (msg) => {
    const ok = window.confirm(`Report this message from ${msg.user_name} to your club admins?\n\n"${msg.body.slice(0, 120)}${msg.body.length > 120 ? '…' : ''}"`);
    if (!ok) return;
    try {
      await axios.post(`/clubs/${cardClubSlug}/events/${ev.id}/messages/${msg.id}/report`);
      setReportedMsgIds(prev => new Set(prev).add(msg.id));
    } catch (err) {
      alert(err.response?.data?.error || 'Could not report the message — please try again.');
    }
  };
  // Who gets their own Going/Not going (Stuart 2026-06-06): players AND
  // coaches/team-managers OF THIS TEAM can register their own attendance.
  // Players count toward the player total; coaches show on a separate line
  // (see attendee lists below). Parents/supporters/admins don't get an own
  // row on team events — they just respond for their children. Club-wide
  // events (no team) show everyone.
  // The user's playing/coaching role on THIS event's team, from the additive
  // teamRoles (v17) — with a fallback to the legacy single role+teamName so
  // existing members keep working until invites/edits write team_members rows.
  const myTeamRoleHere = (() => {
    if (!ev.team_id) return null;
    const tr = (membership?.teamRoles || []).find(r => r.teamId === ev.team_id);
    if (tr) return tr.role;
    const nameMatch = ev.team_name === membership?.teamName || ev.resolved_team_name === membership?.teamName;
    if (nameMatch && ['player', 'coach', 'team_manager'].includes(membership?.role)) return membership.role;
    return null;
  })();
  const userCanAttend = !ev.team_id || ['player', 'coach', 'team_manager'].includes(myTeamRoleHere);
  const isChat = openChatEventId === ev.id;
  // B-54: chat, RSVP buttons and RSVP lists are members-only on the server
  // (403 otherwise). Gate: a signed-in user with an approved membership here
  // (platform staff get an implicit one in /auth/me). The server also admits a
  // team-hat holder with no club row, which shouldn't occur; they'd see the note.
  const isClubMember = !!(user && membership);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleExpand = () => {
    const willExpand = !isExpanded;
    setExpandedEventId(willExpand ? ev.id : null);
    // Thread now lives at the bottom of the expanded card (no more Thread
    // pill), so open it whenever the card opens and close it on collapse.
    setOpenChatEventId(willExpand ? ev.id : null);
  };

  // Prefer stored venue coordinates if we have them.
  // IMPORTANT: pg returns DECIMAL columns as strings (e.g. "-2.7649"). The bbox
  // math below uses + and -, and "-2.7649" + 0.01 is STRING CONCAT ("-2.76490.01"),
  // which produces an invalid bbox and OSM falls back to the world view.
  // Always coerce to Number before doing arithmetic.
  const lat = ev.venue_lat != null ? Number(ev.venue_lat) : null;
  const lng = ev.venue_lng != null ? Number(ev.venue_lng) : null;
  const hasVenueCoords = lat != null && !Number.isNaN(lat) && lng != null && !Number.isNaN(lng);
  const mapsUrl = hasVenueCoords
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : ev.location
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}`
      : null;

  // OpenStreetMap embed (no API key required). Bbox is tight (~600m square)
  // so the venue marker fills the frame rather than getting lost in regional view.
  const BBOX_LAT = 0.004; // ~440m N/S
  const BBOX_LNG = 0.007; // ~480m E/W at UK latitudes
  const osmEmbedUrl = hasVenueCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - BBOX_LNG},${lat - BBOX_LAT},${lng + BBOX_LNG},${lat + BBOX_LAT}&layer=mapnik&marker=${lat},${lng}`
    : null;

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = () => setMenuOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpen]);

  return (
    <div className={`event-card card ${fixtureView === 'past' ? 'event-past' : ''} ${isExpanded ? 'event-expanded' : ''}`}>
      {/* Clickable header row */}
      <div className="event-card-header" onClick={toggleExpand} style={{ cursor: 'pointer' }}>
        <div className="event-icon">{eventTypeIcons[ev.event_type]}</div>
        <div className="event-info">
          {/* Fixtures carry age-grade + home/away in their title (e.g.
              "U11s Taunton Away"), so we drop the badges + "vs opponent" line
              for matches. Training/social keep their type + team badges. */}
          {/* What's on mixes all three kinds, so every card says which it
              is (Fixture / Training / Event), fixtures included. */}
          {(showKind || ev.event_type !== 'match') && (
            <div className="event-header">
              {showKind ? (
                <span className={`badge ${KIND_BADGE[kindLabel(ev.event_type)]}`}>{kindLabel(ev.event_type)}</span>
              ) : (
                <span className="badge" style={{ background: `${categoryColors[ev.event_type] || accentColor}18`, color: categoryColors[ev.event_type] || accentColor }}>
                  {ev.event_type}
                </span>
              )}
              {ev.event_type !== 'match' && (ev.resolved_team_name || ev.team_name) && (
                <span className="badge" style={{ background: `${accentColor}15`, color: accentColor, fontSize: 12 }}>
                  {ev.resolved_team_name || ev.team_name}
                </span>
              )}
            </div>
          )}
          <h3 className="event-title">{ev.title}</h3>
          <div className="event-details">
            {/* v31 — meet time (ticket #7) reads FIRST when set:
                "Sat 6 Sep · Meet 10:00 · KO 10:30" */}
            {ev.meet_at ? (
              <span>
                🕐 {formatEventDateLabel(ev.starts_at)} · <strong>Meet {format(new Date(ev.meet_at), 'HH:mm')}</strong> · {ev.event_type === 'match' ? 'KO' : 'Starts'} {format(parseISO(ev.starts_at), 'HH:mm')}
              </span>
            ) : (
              <span>🕐 {formatEventDate(ev.starts_at)}</span>
            )}
            {ev.location && <span>📍 {ev.location}</span>}
            {/* v31 — respond-by deadline (ticket #11), upcoming only */}
            {ev.rsvp_by && fixtureView !== 'past' && (
              <span className={new Date(ev.rsvp_by) < new Date() ? 'rsvp-deadline-passed' : 'rsvp-deadline'}>
                📣 Respond by {format(new Date(ev.rsvp_by), 'EEE d MMM')}
              </span>
            )}
            {/* B-26 — player-limit pill: players going / cap (coaches/TMs exempt) */}
            {ev.max_participants && fixtureView !== 'past' && (() => {
              const taken = Number(ev.rsvp_players) || 0;
              const full = taken >= ev.max_participants;
              return (
                <span style={{ fontWeight: 600, color: full ? 'var(--danger, #B33A2A)' : undefined }}>
                  👥 {taken}/{ev.max_participants}{full ? ' FULL' : ''}
                </span>
              );
            })()}
            {fixtureView !== 'past' && !isExpanded && (
              <WeatherPill lat={ev.venue_lat} lng={ev.venue_lng} startsAt={ev.starts_at} />
            )}
            {fixtureView !== 'past' && isNextUpcoming && !isExpanded && (
              /* v31 — leave-by targets the MEET time when one is set */
              <LeaveByPill startsAt={ev.meet_at || ev.starts_at} venueLat={ev.venue_lat} venueLng={ev.venue_lng} />
            )}
          </div>
        </div>
        {canManageThisEvent && (
          <div className="event-menu-wrap" onClick={e => e.stopPropagation()}>
            <button
              className="event-menu-btn"
              aria-label="Event options"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="event-menu-popover">
                <button className="event-menu-item" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEditEvent(ev, 'occurrence'); }}>
                  <Pencil size={13} /> {ev.series_id ? 'Edit this occurrence' : 'Edit'}
                </button>
                {ev.series_id && (
                  <button
                    className="event-menu-item"
                    title="Apply changes to this event and every later occurrence in the series"
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEditEvent(ev, 'series'); }}
                  >
                    <Pencil size={13} /> Edit this &amp; future
                  </button>
                )}
                <button className="event-menu-item event-menu-danger" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDeleteEvent(ev, 'occurrence'); }}>
                  <Trash2 size={13} /> {ev.series_id ? 'Delete this occurrence' : 'Delete'}
                </button>
                {ev.series_id && (
                  <>
                    <button
                      className="event-menu-item event-menu-danger"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDeleteEvent(ev, 'future'); }}
                    >
                      <Trash2 size={13} /> Delete this &amp; future
                    </button>
                    <button
                      className="event-menu-item event-menu-danger"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDeleteEvent(ev, 'series'); }}
                    >
                      <Trash2 size={13} /> Delete entire series
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <ChevronDown size={16} className={`event-expand-chevron ${isExpanded ? 'rotated' : ''}`} />
      </div>

      {/* Expanded detail panel */}
      {isExpanded && (
        <div className="event-detail-panel">
          {/* Weather + leave-by detail sit ABOVE the map. The collapsed card
              keeps the compact pills; expanded shows the full detail here once
              (no duplication). */}
          {hasVenueCoords && fixtureView !== 'past' && (
            <WeatherDetail lat={ev.venue_lat} lng={ev.venue_lng} startsAt={ev.starts_at} />
          )}
          {hasVenueCoords && fixtureView !== 'past' && (
            /* v31 — leave-by targets the MEET time when one is set */
            <LeaveByDetail startsAt={ev.meet_at || ev.starts_at} venueLat={ev.venue_lat} venueLng={ev.venue_lng} />
          )}
          {/* Embedded map preview (tap to open native maps) */}
          {osmEmbedUrl && mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="event-map-wrap"
              onClick={e => e.stopPropagation()}
              title="Open in maps"
            >
              <iframe
                className="event-map-iframe"
                src={osmEmbedUrl}
                title={`Map of ${ev.location || 'venue'}`}
                loading="lazy"
              />
              <span className="event-map-overlay">
                <Navigation size={13} /> Tap for directions
              </span>
            </a>
          )}
          {ev.description && <p className="event-desc" style={{ marginBottom: 12 }}>{ev.description}</p>}

          {/* Get Tickets — shown above the RSVP buttons for any ticketed
              upcoming event, for members and non-members alike. */}
          {ev.is_ticketed && ev.tickets_url && fixtureView !== 'past' && (
            <div className="event-detail-actions" style={{ marginBottom: 10 }}>
              <a
                href={ev.tickets_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-rsvp tickets"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                🎫 Get Tickets
              </a>
            </div>
          )}

          {/* RSVP actions — B-54: members only (POST /rsvp 403s others) */}
          {isClubMember && fixtureView !== 'past' && (() => {
            // Find this user's existing response so we can highlight the
            // active button and pre-fill the note input.
            const myYes = rsvpAttendees.yes?.find(p => p.id === user.id);
            const myNo  = rsvpAttendees.no?.find(p => p.id === user.id);
            const myStatus = myYes ? 'yes' : myNo ? 'no' : null;
            const myNote = (myYes || myNo)?.note || '';
            // B-26 — Full state (refined 24 Aug): "player" comes from the
            // event's TEAM SHEET, not the club title — mirrors the server.
            // takenCount uses the server-computed rsvp_players so the pill,
            // banner and enforcement always agree.
            const capacity = ev.max_participants || null;
            const takenCount = Number(ev.rsvp_players) || 0;
            const isFull = capacity && takenCount >= capacity;
            // Am I a player for THIS event? Team events: my roster role on
            // that team ('player' counts — even a club admin; legacy single
            // club-role fallback). Club-wide events: admins/coaches/TMs exempt.
            const staffClubRoles = ['admin', 'coach', 'team_manager'];
            const myEvTeamRole = ev.team_id
              ? ((membership?.teamRoles || []).find(tr => String(tr.teamId) === String(ev.team_id))?.role || null)
              : null;
            const userIsPlayerHere = ev.team_id
              ? (myEvTeamRole ? myEvTeamRole === 'player' : membership?.role === 'player')
              : !(membership?.clubAdmin || staffClubRoles.includes(membership?.role));
            // Does the Full state actually block THIS user's own Going button?
            const fullBlocksMe = isFull && userIsPlayerHere && myStatus !== 'yes';
            return (
          <>
            {/* B-26 — capacity state */}
            {isFull && (
              <p style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: 'var(--danger, #B33A2A)' }}>
                👥 Event full — {capacity === 1 ? 'the 1 player place is' : `all ${capacity} player places`} taken{myStatus === 'yes' ? " (you're in)" : (!userIsPlayerHere ? " (you can still respond — you're not taking a player place)" : '')}.
              </p>
            )}
            {/* v31 — respond-by deadline (ticket #11) */}
            {ev.rsvp_by && (
              <p className={new Date(ev.rsvp_by) < new Date() ? 'rsvp-deadline-passed' : 'rsvp-deadline'} style={{ marginBottom: 8, fontSize: 13 }}>
                📣 {new Date(ev.rsvp_by) < new Date()
                  ? 'The respond-by date has passed — please still respond if you can.'
                  : `Please respond by ${format(new Date(ev.rsvp_by), 'EEEE d MMM')}.`}
              </p>
            )}
            {userCanAttend && (
            <div className="event-detail-actions">
              <button
                className={`btn btn-rsvp yes${myStatus === 'yes' ? ' active' : ''}`}
                disabled={fullBlocksMe}
                title={fullBlocksMe ? 'This event is full' : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  const noteInput = e.currentTarget.closest('.event-detail-panel')?.querySelector('.event-note-input');
                  // WT-22: Not going → Going drops the old excuse ("Family do,
                  // sorry" sat in the Going list). POST /rsvp overwrites the
                  // note with whatever is sent (null clears it), so send none.
                  // A note typed fresh before tapping Going is kept, and the
                  // old one comes back if the change doesn't go through.
                  const typed = noteInput ? noteInput.value : '';
                  if (noteInput && myStatus === 'no' && typed === myNote) {
                    noteInput.value = '';
                  }
                  rsvpMutation.mutate(
                    { eventId: ev.id, status: 'yes', note: noteInput?.value || null },
                    { onError: () => { if (noteInput) noteInput.value = typed; } },
                  );
                }}
              >
                <Check size={13} /> {myStatus === 'yes' ? 'Going ✓' : (fullBlocksMe ? 'Full' : 'Going')}
              </button>
              <button
                className={`btn btn-rsvp no${myStatus === 'no' ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const noteInput = e.currentTarget.closest('.event-detail-panel')?.querySelector('.event-note-input');
                  rsvpMutation.mutate({ eventId: ev.id, status: 'no', note: noteInput?.value || null });
                }}
              >
                <X size={13} /> {myStatus === 'no' ? 'Not going ✓' : 'Not going'}
              </button>
            </div>
            )}

            {/* Optional note attached to the user's response. Pre-filled with
                any existing note; saves on Enter or blur once a status is set.
                Only shown when the user can attend this event themselves. */}
            {userCanAttend && (
            <input
              type="text"
              className="event-note-input"
              defaultValue={myNote}
              placeholder="Add a note (optional) — e.g. 'arriving late' or 'family wedding'"
              maxLength={500}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && myStatus) {
                  e.preventDefault();
                  rsvpMutation.mutate({ eventId: ev.id, status: myStatus, note: e.currentTarget.value });
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => {
                if (myStatus && (e.currentTarget.value || '') !== (myNote || '')) {
                  rsvpMutation.mutate({ eventId: ev.id, status: myStatus, note: e.currentTarget.value });
                }
              }}
            />
            )}

            {/* Per-family-member RSVP — a row per child so a parent can
                respond on each child's behalf (v14 onBehalfOfUserId). The
                RSVP belongs to the child; responded_by captures the parent.
                Only children actually invited to this event are shown: a
                club-wide event shows all; a team event shows only children
                on that team. */}
            {(() => {
              const relevantChildren = (myChildren || []).filter(
                c => !ev.team_id || (c.teams || []).some(t => t.id === ev.team_id)
              );
              if (relevantChildren.length === 0) return null;
              return (
              <div className="rsvp-children">
                {relevantChildren.map(child => {
                  const cYes = rsvpAttendees.yes?.find(p => p.id === child.id);
                  const cNo  = rsvpAttendees.no?.find(p => p.id === child.id);
                  const cStatus = cYes ? 'yes' : cNo ? 'no' : null;
                  return (
                    <div key={child.id} className="rsvp-child-block">
                      <div className="rsvp-child-name">{child.firstName} {child.lastName}</div>
                      <div className="rsvp-child-actions">
                        <button
                          className={`btn btn-rsvp yes${cStatus === 'yes' ? ' active' : ''}`}
                          disabled={isFull && cStatus !== 'yes'}
                          title={isFull && cStatus !== 'yes' ? 'This event is full' : undefined}
                          onClick={(e) => { e.stopPropagation(); rsvpMutation.mutate({ eventId: ev.id, status: 'yes', onBehalfOfUserId: child.id }); }}
                        >
                          <Check size={13} /> {cStatus === 'yes' ? 'Going ✓' : (isFull ? 'Full' : 'Going')}
                        </button>
                        <button
                          className={`btn btn-rsvp no${cStatus === 'no' ? ' active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); rsvpMutation.mutate({ eventId: ev.id, status: 'no', onBehalfOfUserId: child.id }); }}
                        >
                          <X size={13} /> {cStatus === 'no' ? 'Not going ✓' : 'Not going'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              );
            })()}
          </>
          );
          })()}

          {/* Share to WhatsApp (Oct 2026): signed-out visitors arrive here
              from shared links — give them the way to reply. */}
          {!user && fixtureView !== 'past' && (
            <div className="event-signin-cta">
              <span>Is this you or your child? Sign in to say if you're going.</span>
              <Link
                to="/login"
                state={{ from: `/${cardClubSlug}?event=${ev.id}` }}
                className="btn btn-primary btn-sm"
                onClick={(e) => e.stopPropagation()}
              >
                Sign in to reply
              </Link>
            </div>
          )}

          {/* Share to WhatsApp (Oct 2026) — drop the fixture into the team
              group with a tap-to-reply link back here. Upcoming only. */}
          {fixtureView !== 'past' && (
            <div className="event-share-row">
              <ShareMenu
                text={eventShareText(ev, eventShareUrl(cardClubSlug, ev.id))}
                clubSlug={cardClubSlug} kind="event" targetId={ev.id}
                label={ev.event_type === 'match' ? 'Share fixture' : 'Share'}
              />
            </div>
          )}

          {/* Directions only (past events / non-members) */}
          {mapsUrl && (!user || fixtureView === 'past') && (
            <div className="event-detail-actions">
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-rsvp"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Navigation size={13} /> Get Directions
              </a>
            </div>
          )}


          {/* Attendee lists — show note in italic after each name when present.
              Players (incl. children) drive the headline count; coaches/managers
              are listed separately so they never inflate the player numbers. */}
          {!isClubMember && (
            <div className="event-attendees">
              <div className="attendee-group">
                <div className="attendee-names empty">
                  Join the club to see the chat and who's going.
                </div>
              </div>
            </div>
          )}
          {isClubMember && (() => {
            // Numbered lists (ticket queue, Dominique F-004): admins/coaches
            // want a countable roll-call, not a comma-wall of names.
            // WT-29 (CEO, 17 Oct): the server hides a child's name unless the
            // viewer is a club admin, the child's parent, an active coach /
            // team manager of a team the child is on, or (WT-29b) the child's
            // teammate or a teammate's parent (rows marked `redacted`, named
            // "Junior player"); a sponsor account sees "Club member" for
            // everyone on club lists. Hidden rows come last; they're grouped into one line,
            // e.g. "3 junior players". The counts above them are unchanged.
            const plural = (n, one) => (n === 1 ? `1 ${one}` : `${n} ${one}s`);
            const hiddenLine = (hidden) => {
              const members = hidden.filter(p => p.name === 'Club member').length;
              const juniors = hidden.length - members;
              return [juniors && plural(juniors, 'junior player'), members && plural(members, 'club member')]
                .filter(Boolean).join(' and ');
            };
            const renderList = (people) => {
              const named = people.filter(p => !p.redacted);
              const hidden = people.filter(p => p.redacted);
              return (
                <>
                  {named.length > 0 && (
                    <ol className="attendee-list" style={{ margin: 0, paddingLeft: 22 }}>
                      {named.map(p => (
                        <li key={p.id} className="attendee-item">
                          {p.name}
                          {p.note ? <em className="attendee-note"> — {p.note}</em> : null}
                        </li>
                      ))}
                    </ol>
                  )}
                  {hidden.length > 0 && (
                    <div className="attendee-juniors">
                      {named.length > 0 ? `+ ${hiddenLine(hidden)}` : hiddenLine(hidden)}
                    </div>
                  )}
                </>
              );
            };
            const allRows = [...rsvpAttendees.yes, ...rsvpAttendees.no, ...(rsvpAttendees.noResponse || [])];
            const hiddenForSponsor = allRows.some(p => p.redacted && p.name === 'Club member');
            const hasHiddenJuniors = allRows.some(p => p.redacted);
            const isStaff = p => !p.isChild && ['coach', 'team_manager'].includes(p.role);
            const yesPlayers = rsvpAttendees.yes.filter(p => !isStaff(p));
            const noPlayers  = rsvpAttendees.no.filter(p => !isStaff(p));
            const yesStaff   = rsvpAttendees.yes.filter(isStaff);
            return (
          <div className="event-attendees">
            <div className="attendee-group">
              <span className="attendee-label going">✓ Going ({yesPlayers.length})</span>
              {yesPlayers.length > 0 ? (
                <div className="attendee-names">{renderList(yesPlayers)}</div>
              ) : (
                <div className="attendee-names empty">No responses yet</div>
              )}
            </div>
            <div className="attendee-group">
              <span className="attendee-label not-going">✗ Not going ({noPlayers.length})</span>
              {noPlayers.length > 0 && (
                <div className="attendee-names">{renderList(noPlayers)}</div>
              )}
            </div>
            {yesStaff.length > 0 && (
              <div className="attendee-group">
                <span className="attendee-label coaches">🧑‍🏫 Coaches ({yesStaff.length})</span>
                <div className="attendee-names">{renderList(yesStaff)}</div>
              </div>
            )}
            {canChaseRsvp && rsvpAttendees.noResponse && rsvpAttendees.noResponse.length > 0 && (
              <div className="attendee-group">
                <span className="attendee-label no-response">⏳ No response ({rsvpAttendees.noResponse.length})</span>
                <div className="attendee-names muted">{renderList(rsvpAttendees.noResponse)}</div>
                {/* v31/B-28 — manual reminder push to non-responders (children
                    route to parents). Server rate-limits to once per 4h. */}
                {fixtureView !== 'past' && remindMutation && canManageThisEvent && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: 8 }}
                    disabled={remindMutation.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(rsvpAttendees.noResponse.length === 1
                        ? "Send a push reminder to the 1 person who hasn't responded?"
                        : `Send a push reminder to the ${rsvpAttendees.noResponse.length} people who haven't responded?`)) {
                        remindMutation.mutate(ev.id);
                      }
                    }}
                  >
                    🔔 {remindMutation.isPending ? 'Sending…' : 'Remind non-responders'}
                  </button>
                )}
              </div>
            )}
            {/* W-9: the chase list + remind button only exist when someone is
                expected and hasn't answered. A manager whose team has nobody
                on the roster saw nothing at all — say why. */}
            {canChaseRsvp && Array.isArray(rsvpAttendees.noResponse) && rsvpAttendees.noResponse.length === 0
              && yesPlayers.length === 0 && noPlayers.length === 0 && ev.team_id && (
              <div className="attendee-group">
                <div className="attendee-names empty">
                  No one is on this team's roster yet — add players (or their parents) in Club Admin → Members and the chase list and reminders appear here.
                </div>
              </div>
            )}
            {/* WT-29b: the copy follows the revised rule. A sponsor still sees
                "Club member" here (club content), but now sees names at the
                till and in Attribution, so the note says "club lists". */}
            {hasHiddenJuniors && (
              <p className="attendee-privacy-note">
                {hiddenForSponsor
                  ? "Sponsor accounts don't see members' names on club lists like this one."
                  : "Children's names are only shown to their parents, their teammates and teammates' parents, their team's coaches and managers, and club admins."}
              </p>
            )}
          </div>
            );
          })()}

          {/* B-27 — attendance register: the TM/coach of this event's team or a
              club admin, once the event has started (server re-checks both). */}
          {user && canManageThisEvent && eventHasStarted(ev) && (
            <AttendanceRegister clubSlug={cardClubSlug} ev={ev} />
          )}

          {/* Thread */}
          {isChat && isClubMember && (
            <div className="event-chat">
              <div className="event-chat-messages">
                {chatMessages.length === 0 && <div className="event-chat-empty">No messages yet — be the first!</div>}
                {chatOlderCount > 0 && (
                  <div className="event-chat-empty" style={{ fontSize: 12 }}>
                    {chatOlderCount} earlier {chatOlderCount === 1 ? 'message' : 'messages'} not shown.
                  </div>
                )}
                {chatMessages.map(msg => (
                  <div key={msg.id} className={`chat-msg ${msg.user_id === user.id ? 'mine' : ''}`}>
                    <div className="chat-msg-header">
                      <span className="chat-msg-name">{msg.user_name}</span>
                      <span className="chat-msg-time">{format(parseISO(msg.created_at), 'HH:mm · d MMM')}</span>
                      {/* UGC report flag (Play policy) — others' messages only */}
                      {msg.user_id !== user.id && (
                        reportedMsgIds.has(msg.id) ? (
                          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>Reported ✓</span>
                        ) : (
                          <button
                            title="Report this message to club admins"
                            aria-label="Report message"
                            onClick={(e) => { e.stopPropagation(); reportMessage(msg); }}
                            style={{
                              marginLeft: 'auto', background: 'transparent', border: 'none',
                              color: 'var(--text-muted)', padding: 2, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', opacity: 0.55,
                            }}
                          >
                            <Flag size={12} />
                          </button>
                        )
                      )}
                    </div>
                    <div className="chat-msg-body">{msg.body}</div>
                  </div>
                ))}
              </div>
              <div className="event-chat-input">
                <input
                  className="input"
                  placeholder="Write a message…"
                  value={chatMessage}
                  onChange={e => setChatMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && chatMessage.trim()) sendMessageMutation.mutate(chatMessage); }}
                  onClick={e => e.stopPropagation()}
                />
                <button className="btn btn-primary btn-sm" disabled={!chatMessage.trim()}
                  onClick={(e) => { e.stopPropagation(); sendMessageMutation.mutate(chatMessage); }}>
                  <Send size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collapsed footer — show counts even when collapsed */}
      {!isExpanded && (
        <div className="event-footer-collapsed" onClick={toggleExpand} style={{ cursor: 'pointer' }}>
          {/* WT-28: "1 player", not "1 players". */}
          <span className="rsvp-count green">👥 {plural(ev.rsvp_players || 0, 'player')}</span>
          <span className="rsvp-count coaches">🧑‍🏫 {plural(ev.rsvp_coaches || 0, 'coach', 'coaches')}</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>Tap for details</span>
        </div>
      )}
    </div>
  );
};

// WT-4: "You asked on 16 Oct as a Parent / Guardian." for a waiting request
// (GET /me/join-requests row). A Coach request is stored as Supporter (B-58).
const JOIN_ROLE_LABELS = { player: 'Player', parent: 'Parent / Guardian', supporter: 'Supporter' };
const joinRequestSummary = (req) => {
  const when = req?.requested_at ? new Date(req.requested_at) : null;
  const on = when && !Number.isNaN(when.getTime())
    ? ` on ${when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : '';
  const as = JOIN_ROLE_LABELS[req?.role] ? ` as a ${JOIN_ROLE_LABELS[req.role]}` : '';
  return `You asked${on}${as}.`;
};

const ClubHub = () => {
  const { clubSlug } = useParams();
  const { user, getMembership, loading: authLoading } = useAuth();
  const membership = getMembership(clubSlug);
  const qc = useQueryClient();

  // Section + fixture view state
  // What's on (Stuart, 29 Oct): everyone opens the hub on it.
  const [sectionView, setSectionView] = useState('whatson'); // 'whatson' | 'fixtures' | 'training' | 'news' | 'events'
  // Edit from What's on opens the form on the event's own tab; Save or
  // Cancel brings the person back to What's on (QA, 29 Oct).
  const editReturnRef = useRef(null);
  const [fixtureView, setFixtureView] = useState('upcoming'); // 'upcoming' | 'past'
  // Members only (Stuart, 12 Oct): anything past, and every team fixture and
  // training session — no kids' fixtures for strangers. The server enforces it
  // (GET /events returns strangers upcoming club-wide events only).
  const canSeePast = !!membership;
  useEffect(() => {
    if (!canSeePast && fixtureView === 'past') setFixtureView('upcoming');
  }, [canSeePast, fixtureView]);

  // (Sponsor redirect lives AFTER all hooks — see below, before the return.)

  // Capabilities from the UNION of a person's hats (v17 additive model):
  // club admin + per-team roles, with a fallback to the legacy single role so
  // existing members behave exactly as before until invites write team_members.
  const clubAdmin = membership?.clubAdmin || membership?.role === 'admin';
  const teamRoles = membership?.teamRoles || [];
  const legacyTeamRole = ['team_manager', 'coach', 'player'].includes(membership?.role) ? membership.role : null;
  const hasTeamRole = (roles) =>
    teamRoles.some(tr => roles.includes(tr.role)) || (legacyTeamRole && roles.includes(legacyTeamRole));
  // canManage: create/edit/delete news announcements — club admin or team manager
  const canManage = clubAdmin || hasTeamRole(['team_manager']);
  // canPost alias kept for any remaining references (same as canManage now)
  const canPost = canManage;
  // canManageEvents (24 Aug, Stuart): coaches ALSO create/edit/delete
  // fixtures, training and events — they run the team day-to-day. Matches
  // the backend event routes; announcements stay admin/TM.
  const canManageEvents = clubAdmin || hasTeamRole(['team_manager', 'coach']);
  // canChaseRsvp: see non-responders — club admin, team manager or coach
  const canChaseRsvp = clubAdmin || hasTeamRole(['team_manager', 'coach']);
  const canClubWide = clubAdmin || membership?.role === 'team_manager'
    || (membership?.teamRoles || []).some(tr => tr.role === 'team_manager');

  const [joinForm, setJoinForm] = useState({ role: 'supporter', teamName: '' });
  const [showJoin, setShowJoin] = useState(false);
  // W-2: verification banner for admins of a club that hasn't submitted yet.
  // Dismiss is per-session (sessionStorage) — it's a nudge, not a nag, but
  // it should come back next visit until they've actually submitted.
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem(`marakin_verify_banner:${clubSlug}`) === '1'; } catch { return false; }
  });
  const dismissVerifyBanner = () => {
    setVerifyBannerDismissed(true);
    try { sessionStorage.setItem(`marakin_verify_banner:${clubSlug}`, '1'); } catch { /* storage unavailable */ }
  };
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', body: '', category: 'general', teamId: '', isPinned: false });
  const [showNewAnn, setShowNewAnn] = useState(false);
  // B-52.2: the post just made, so the poster can share it straight out
  // (post once in Marakin, then WhatsApp / the phone's share sheet).
  const [justPosted, setJustPosted] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', description: '', eventType: 'training', location: '', startsAt: '', endsAt: '', isHome: null, opponent: '', opponentClubId: null, selectedOpponent: null, venueLat: null, venueLng: null, teamName: '', teamId: '', titleDirty: false, locationDirty: false, isTicketed: false, ticketsUrl: '' });
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null); // null = creating, uuid = editing
  // The create/edit event form renders at the top of its section. When it
  // opens (especially on Edit) it can sit above the fold, so it looks like
  // nothing happened. Same ref is attached to all three event forms — only
  // one is mounted at a time, so it always points at the visible form.
  // Walkthrough flag 7 (Stuart, 29 Oct): "The user can scroll down as they
  // need… Don't need the app doing it automatically." So the page only moves
  // when the form's top is OFF screen (Edit from a card further down);
  // pressing Add — form right under the button — leaves the page alone.
  const eventFormRef = useRef(null);
  useEffect(() => {
    if (!showNewEvent) return;
    const t = setTimeout(() => {
      const el = eventFormRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const navH = document.querySelector('.navbar')?.getBoundingClientRect().height || 64;
      if (top < navH || top > window.innerHeight - 120) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [showNewEvent, editingEventId, sectionView]);
  const [openChatEventId, setOpenChatEventId] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [supportDismissed, setSupportDismissed] = useState(false); // floating "Support the club" bar — state only, no storage (keeps cookie assessment clean)
  // 25 Aug (Stuart): "Needs your response" was swamping the first screen —
  // now capped at 2 rows with an expander + a close button. Same state-only,
  // no-storage policy as supportDismissed: the nudge SHOULD come back on the
  // next visit while events are still unanswered.
  const [nudgeExpanded, setNudgeExpanded] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);

  const { data: club, isLoading: clubLoading } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data)
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['teams', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/teams`).then(r => r.data)
  });

  // v31/B-30 scoping (Stuart, 24 Aug): TMs/coaches only manage entries for
  // teams they are TM/coach OF — a player hat on another team grants nothing
  // there. The event-form team dropdowns only offer teams the person can
  // manage; club-wide entries are admin + team-manager only. Legacy members
  // with no roster rows keep club-role behaviour. Server enforces the same.
  // (Declared AFTER the teams query — referencing it earlier crashed with a
  // temporal-dead-zone ReferenceError.)
  const manageableTeams = useMemo(() => {
    if (clubAdmin) return teams;
    const roster = membership?.teamRoles || [];
    if (roster.length === 0) return teams; // legacy — server still guards
    const ids = new Set(roster.filter(tr => ['team_manager', 'coach'].includes(tr.role)).map(tr => String(tr.teamId)));
    return teams.filter(t => ids.has(String(t.id)));
    // eslint-disable-next-line
  }, [teams, clubAdmin, membership]);
  // WT-21: one team to choose from (a TM or coach of one team) → the add
  // fixture / session forms start with it picked.
  const onlyTeamPreset = manageableTeams.length === 1
    ? { teamId: manageableTeams[0].id, teamName: manageableTeams[0].name }
    : { teamId: '', teamName: '' };

  // (W-19: the "your team by default" logic now lives below the my-children
  // query, because a parent's team is their children's team.)

  // B-24 (26 Aug): news was a bare LIMIT-20 array and the section badge
  // rendered `announcements.length`, so it read "20" forever while older posts
  // became unreachable. Now paged, with the true total from X-Total-Count.
  const NEWS_PAGE = 20;
  const NEWS_MAX = 300; // must match ANNOUNCEMENTS_MAX in comms.js
  const [newsLimit, setNewsLimit] = useState(NEWS_PAGE);
  useEffect(() => { setNewsLimit(NEWS_PAGE); }, [selectedTeamFilter]);

  // B-35: user?.id in the key — on a hard load this query fires before
  // AuthProvider sets the bearer, so the route (optionalAuthenticate) answers
  // anonymously and never logs announcements_viewed. Refetch once signed in.
  const { data: newsPage } = useQuery({
    queryKey: ['announcements', clubSlug, selectedTeamFilter, newsLimit, user?.id],
    enabled: !authLoading,
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedTeamFilter !== 'all') params.set('teamId', selectedTeamFilter);
      params.set('limit', String(newsLimit));
      return fetchList(`/clubs/${clubSlug}/announcements?${params.toString()}`);
    },
    placeholderData: (prev) => prev, // keep the list up while a bigger page loads
  });
  const announcements = newsPage?.items ?? [];
  const announcementsTotal = newsPage?.total ?? announcements.length;
  const announcementsHasMore = newsPage?.hasMore ?? false;

  // F-009: each hub section (Fixtures / Training / Events) fetches its own
  // event type with real pagination. The old single LIMIT-30 list meant a
  // 10+ team club bulk-loading a season silently hid far-future events —
  // near-term training filled all 30 slots. Per-type paging keeps payloads
  // small (load speed flat) while lists can grow to a full season; 'Show
  // more' bumps the per-section limit by a page.
  const EVENTS_PAGE = 100;
  const EVENTS_MAX = 300; // must match the server-side clamp in comms.js
  const [eventLimits, setEventLimits] = useState({ match: EVENTS_PAGE, training: EVENTS_PAGE, other: EVENTS_PAGE });
  const showMoreEvents = (type) => setEventLimits(p => ({ ...p, [type]: Math.min(p[type] + EVENTS_PAGE, EVENTS_MAX) }));
  const eventsQueryFor = (type, limit) => ({
    // Members see more than strangers, so wait for sign-in and key on who's
    // asking — a cold load must never cache the stranger list for a member.
    queryKey: ['events', clubSlug, selectedTeamFilter, fixtureView, type, limit, user?.id],
    enabled: !authLoading,
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedTeamFilter !== 'all') params.set('teamId', selectedTeamFilter);
      if (fixtureView === 'past') params.set('view', 'past');
      params.set('type', type);
      params.set('limit', String(limit));
      return fetchList(`/clubs/${clubSlug}/events?${params.toString()}`);
    },
    placeholderData: (prev) => prev, // keep the list on screen while a bigger page loads
  });
  // B-24: hasMore now comes from the server. It used to be inferred as
  // `rows.length >= limit`, which shows a phantom "Show more" whenever the
  // count lands exactly on a page boundary (100, 200, 300).
  const fixturePage = useQuery(eventsQueryFor('match', eventLimits.match)).data;
  const trainingPage = useQuery(eventsQueryFor('training', eventLimits.training)).data;
  const otherPage = useQuery(eventsQueryFor('other', eventLimits.other)).data;
  // useMemo so each list keeps a stable reference while the queries are
  // loading — otherwise `?? []` mints a new array every render and the merged
  // `events` memo below recomputes on every one.
  const fixtureEvents = useMemo(() => fixturePage?.items ?? [], [fixturePage]);
  const trainingEvents = useMemo(() => trainingPage?.items ?? [], [trainingPage]);
  const otherEvents = useMemo(() => otherPage?.items ?? [], [otherPage]);
  const eventsMeta = {
    match: { total: fixturePage?.total ?? fixtureEvents.length, hasMore: fixturePage?.hasMore ?? false },
    training: { total: trainingPage?.total ?? trainingEvents.length, hasMore: trainingPage?.hasMore ?? false },
    other: { total: otherPage?.total ?? otherEvents.length, hasMore: otherPage?.hasMore ?? false },
  };
  // Merged view for consumers that search across all types (deep links etc.).
  const events = useMemo(
    () => [...fixtureEvents, ...trainingEvents, ...otherEvents],
    [fixtureEvents, trainingEvents, otherEvents]
  );

  // What's on: every kind in one upcoming list (no type → the server returns
  // fixtures, training and events together, soonest first; same team filter
  // and who-sees-what as the per-type lists). Always upcoming, whatever the
  // other tabs' Upcoming / Past switch says.
  const [whatsOnLimit, setWhatsOnLimit] = useState(EVENTS_PAGE);
  const { data: whatsOnPage, isError: whatsOnError } = useQuery({
    queryKey: ['events', clubSlug, selectedTeamFilter, 'upcoming', 'all', whatsOnLimit, user?.id],
    enabled: !authLoading,
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedTeamFilter !== 'all') params.set('teamId', selectedTeamFilter);
      params.set('limit', String(whatsOnLimit));
      return fetchList(`/clubs/${clubSlug}/events?${params.toString()}`);
    },
    placeholderData: (prev) => prev,
  });
  const whatsOnEvents = useMemo(() => whatsOnPage?.items ?? [], [whatsOnPage]);
  const todayKey = new Date().toDateString(); // regroup when the date changes
  // eslint-disable-next-line
  const whatsOnDays = useMemo(() => groupByDay(whatsOnEvents), [whatsOnEvents, todayKey]);
  const whatsOnMeta = { total: whatsOnPage?.total ?? whatsOnEvents.length, hasMore: whatsOnPage?.hasMore ?? false };

  // (Deep links — push notifications and shared links — are handled further
  // down, after the team-filter default they have to pre-empt.)

  // Parent's children at this club — used to render per-child RSVP rows on
  // event cards. Empty for non-parents, so the UI naturally hides for them.
  const { data: myChildrenData } = useQuery({
    queryKey: ['my-children', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/my-children`).then(r => r.data),
    enabled: !!user && !!membership,   // B-54: members only (server 403s others)
  });
  // B-90 (QA S1): a team place still waiting for the team manager isn't the
  // child's team yet — no RSVP buttons for it, and it doesn't pick the
  // default team filter.
  const myChildren = useMemo(() => (myChildrenData?.children || []).map(c => ({
    ...c, teams: (c.teams || []).filter(t => !t.pending),
  })), [myChildrenData]);

  // W-19 (Stuart, 14 Sept): land on YOUR team by default. "Your" team = the
  // teams you hold a hat on (teamRoles / legacy teamName) PLUS the teams your
  // children are on. Exactly one → that team is the default (club-wide posts
  // still surface — the API's team filter keeps team_id IS NULL rows).
  // Genuinely multi-team, or a club admin who runs the whole club → All
  // Teams. The filter is a real select for everyone, so a defaulted member
  // can still flick to All Teams; we only set the default once and never
  // override a choice they've made.
  const myTeamIds = [...new Set([
    ...teamRoles.map(tr => tr.teamId).filter(Boolean),
    ...(membership?.teamName ? teams.filter(t => t.name === membership.teamName).map(t => t.id) : []),
    ...myChildren.flatMap(c => (c.teams || []).map(t => t.id)).filter(Boolean),
  ])];
  const myTeam = myTeamIds.length === 1 ? teams.find(t => String(t.id) === String(myTeamIds[0])) : null;
  const filterDefaulted = useRef(false);
  const filterTouched = useRef(false);
  useEffect(() => {
    if (filterDefaulted.current || filterTouched.current) return;
    if (user && !myChildrenData) return; // wait for the children query — a parent's team is their children's
    if (myTeam && !clubAdmin) {
      filterDefaulted.current = true;
      setSelectedTeamFilter(myTeam.id);
    }
  // eslint-disable-next-line
  }, [myTeam?.id, clubAdmin, myChildrenData]);

  // Deep links: /<club>?event=<id>[&tab=fixtures|training|events] (push
  // notifications, and shared links via /:club/e/:id) and /<club>?news=<id>
  // (shared news via /:club/n/:id — pages/SharedLinkRedirect.jsx).
  // Two entry paths must both work:
  //   1. Cold start — OS notification tap / WhatsApp link loads the page with
  //      the query present.
  //   2. In-app — the foreground PushBanner calls navigate(url) while we're
  //      already mounted, so the effect re-runs on each navigation (location
  //      key — a repeat banner for the same event opens it again).
  // The target is parked in state and the URL cleaned at once. Share to
  // WhatsApp (Oct 2026): a shared fixture is often NOT in the lists the hub
  // first loads — another team's filter, the Past tab, a training session
  // while Fixtures is showing — so if the loaded lists don't have it we ask
  // the server where it lives (GET /shares/event/:id), switch tab / view /
  // team to match, and open the card when its list arrives. Still missing →
  // load that list's maximum once, then say so. A link that can't be opened
  // always says why (deepLinkNotice) — never a silent no-op.
  const routerLocation = useLocation();
  const [deepLink, setDeepLink] = useState(null); // { kind: 'event'|'news', id, located?, listType?, widened? }
  const [deepLinkNotice, setDeepLinkNotice] = useState(null);
  // A shared link to a team fixture/training or a past event, opened by a
  // stranger: the server never sends them those, so say why and offer the way
  // in (sign in, or ask to join) instead of "couldn't open that one".
  const [memberOnlyLink, setMemberOnlyLink] = useState(null); // { eventId | newsId, what }
  // Becoming a member (approved, or signed in) → fetch the member lists now,
  // not after the 30s cache.
  useEffect(() => {
    if (canSeePast) {
      qc.invalidateQueries({ queryKey: ['events', clubSlug] });
      qc.invalidateQueries({ queryKey: ['announcements', clubSlug] });
      // A members-only link they couldn't open as a stranger: open it now.
      if (memberOnlyLink) {
        setDeepLink(memberOnlyLink.newsId
          ? { kind: 'news', id: memberOnlyLink.newsId }
          : { kind: 'event', id: memberOnlyLink.eventId, located: false });
        setMemberOnlyLink(null);
      }
    }
    // eslint-disable-next-line
  }, [canSeePast, clubSlug]);
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search || window.location.search);
    const eventId = params.get('event');
    const newsId = params.get('news');
    const tabOnly = params.get('tab');
    if (!eventId && !newsId) {
      // /<club>?tab=fixtures|… on its own: open that tab (the guided tours
      // use it to show the Add buttons, which live on their own tabs).
      if (tabOnly && ['whatson', 'fixtures', 'training', 'news', 'events'].includes(tabOnly)) {
        setSectionView(tabOnly);
        window.history.replaceState({}, '', `/${clubSlug}`);
      }
      return;
    }
    setDeepLinkNotice(null);
    setMemberOnlyLink(null);
    if (eventId) {
      const tab = params.get('tab');
      if (tab && ['fixtures', 'training', 'events'].includes(tab)) setSectionView(tab);
      setDeepLink({ kind: 'event', id: eventId, located: false });
    } else {
      // News is filtered by team too — show everything so the post can be found.
      filterTouched.current = true;
      setSelectedTeamFilter('all');
      setSectionView('news');
      setDeepLink({ kind: 'news', id: newsId });
    }
    // Clean the URL (keep the path, drop the query) without a navigation.
    window.history.replaceState({}, '', `/${clubSlug}`);
  }, [clubSlug, routerLocation.key, routerLocation.search]);

  const eventListsLoaded = !!(fixturePage && trainingPage && otherPage);
  useEffect(() => {
    if (deepLink?.kind !== 'event') return undefined;
    if (authLoading) return undefined; // don't judge "stranger" before sign-in has loaded
    const sameLink = (d) => d?.id === deepLink.id;
    const found = events.find(e => String(e.id) === String(deepLink.id));
    if (found) {
      // Pin the team filter only for a team the person doesn't follow; for
      // their own team (or a club-wide entry) "your team by default" (W-19)
      // can still apply — the card stays in that list.
      if (found.team_id && !myTeamIds.some(t => String(t) === String(found.team_id))) filterTouched.current = true;
      // Shared links carry no ?tab= — the event's own type picks the tab.
      setSectionView(sectionForEventType(found.event_type));
      setExpandedEventId(deepLink.id);
      setDeepLink(null);
      setTimeout(() => {
        document.querySelector('.event-expanded')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      return undefined;
    }
    if (!eventListsLoaded) return undefined;
    if (deepLink.located) {
      // Switched to where it lives and still not there after the list
      // settles: probably past the loaded page. Load that list's maximum
      // once; if that doesn't bring it in, say so.
      const t = setTimeout(() => {
        if (!deepLink.widened) {
          setEventLimits(p => ({ ...p, [deepLink.listType]: EVENTS_MAX }));
          setDeepLink(d => (sameLink(d) ? { ...d, widened: true } : d));
        } else {
          setDeepLink(d => (sameLink(d) ? null : d));
          setDeepLinkNotice("We couldn't open that one here — it may be further down the list.");
        }
      }, deepLink.widened ? 6000 : 3000);
      return () => clearTimeout(t);
    }
    let cancelled = false;
    axios.get(`/clubs/${clubSlug}/shares/event/${encodeURIComponent(deepLink.id)}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!membership && (data.team_id || data.is_past || data.event_type === 'match' || data.event_type === 'training')) {
          setDeepLink(null);
          setSectionView(sectionForEventType(data.event_type));
          setMemberOnlyLink({ eventId: deepLink.id, what: data.event_type === 'training' ? 'training session' : data.event_type === 'match' ? 'fixture' : 'event' });
          return;
        }
        setSectionView(sectionForEventType(data.event_type));
        setFixtureView(data.is_past ? 'past' : 'upcoming');
        if (data.team_id) {
          filterTouched.current = true;
          setSelectedTeamFilter(String(data.team_id));
        }
        const listType = data.event_type === 'match' || data.event_type === 'training' ? data.event_type : 'other';
        setDeepLink(d => (sameLink(d) ? { ...d, located: true, listType } : d));
      })
      .catch((err) => {
        if (cancelled) return;
        setDeepLink(null);
        setDeepLinkNotice(err.response?.status === 404
          ? "That fixture or session isn't on the calendar any more — it may have been cancelled."
          : "We couldn't open that link — please try again.");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [deepLink, events, eventListsLoaded, authLoading]);

  // Shared news post: News tab is already showing; scroll to the post and
  // flash it. An older post beyond the loaded page gets a pointer instead.
  useEffect(() => {
    if (deepLink?.kind !== 'news' || !newsPage || authLoading) return;
    const { id } = deepLink;
    setDeepLink(null);
    if (!announcements.some(a => String(a.id) === String(id)) && !membership && !announcementsHasMore) {
      // Strangers only get club-wide news, so a team post can't be here.
      setMemberOnlyLink({ newsId: id, what: 'post' });
      return;
    }
    if (!announcements.some(a => String(a.id) === String(id))) {
      setDeepLinkNotice(announcementsHasMore
        ? "That post is further down — tap “Show older news” below."
        : "That post isn't here any more — it may have been taken down.");
      return;
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-ann-id="${id}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('share-target');
      setTimeout(() => el.classList.remove('share-target'), 2500);
    }, 300);
  }, [deepLink, newsPage, announcements, announcementsHasMore, authLoading]);

  // B-58: the modal shows the outcome itself (sent / already asked / why not)
  // instead of a browser alert; the server's message is the source of truth.
  const joinMutation = useMutation({
    mutationFn: () => axios.post(`/clubs/${clubSlug}/join`, joinForm),
    // WT-4: the banner switches to "Waiting for an admin" straight away.
    // "Already a member" (approved meanwhile) → pick up the membership.
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['join-requests'] });
      if (res?.data?.status === 'already_member') refreshUser();
    },
  });
  const closeJoin = () => { setShowJoin(false); joinMutation.reset(); };
  // WT-28: Esc closes the Join pop-up, like its ✕ and a tap outside it.
  useEffect(() => {
    if (!showJoin) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeJoin(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [showJoin]);

  // WT-4 (16 Oct walk-through; B-58 slice 3's waiting card): /auth/me lists
  // approved clubs only, so a waiting request looked exactly like none: the
  // banner still said "Request to join" and asking again said "You've already
  // asked". GET /me/join-requests (B-58) says which clubs are waiting; the
  // same query key feeds My Clubs. Withdraw is DELETE /clubs/:slug/join
  // (own waiting row only); asking again keeps every B-58 rule server-side.
  const { refreshUser } = useAuth();
  const { data: myJoinRequests = [], isPending: joinRequestsPending } = useQuery({
    queryKey: ['join-requests'],
    queryFn: () => axios.get('/me/join-requests').then(r => (Array.isArray(r.data) ? r.data : [])),
    enabled: !!user && !membership && !user.isPlatformUser && !authLoading,
    staleTime: 30000,
  });
  const waitingRequest = (user && !membership && myJoinRequests.find(r => r.club_slug === clubSlug)) || null;
  const withdrawMutation = useMutation({
    mutationFn: () => axios.delete(`/clubs/${clubSlug}/join`),
    onSuccess: () => {
      joinMutation.reset();
      // Drop it from the cache at once, so Withdraw can't be pressed twice.
      qc.setQueryData(['join-requests'], old => (old || []).filter(r => r.club_slug !== clubSlug));
      qc.invalidateQueries({ queryKey: ['join-requests'] });
    },
    // Not waiting any more (an admin answered meanwhile): show the server's
    // message and pick up whatever it is now (approved → member view).
    onError: (err) => {
      if (err?.response?.status === 404) { qc.invalidateQueries({ queryKey: ['join-requests'] }); refreshUser(); }
    },
  });
  const withdrawJoinRequest = () => {
    if (!window.confirm(`Withdraw your request to join ${club?.name || 'this club'}? You can ask again later.`)) return;
    withdrawMutation.mutate();
  };
  const withdrawError = withdrawMutation.isError
    ? (withdrawMutation.error?.response?.data?.error || 'We couldn\'t withdraw your request. Please try again.')
    : null;

  const rsvpMutation = useMutation({
    mutationFn: ({ eventId, status, note, onBehalfOfUserId }) => axios.post(
      `/clubs/${clubSlug}/events/${eventId}/rsvp`,
      { status, note: note ?? null, onBehalfOfUserId: onBehalfOfUserId ?? null }
    ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['events', clubSlug] });
      // Refresh the expanded event's attendee list so the user sees their
      // own response (and any note) reflected immediately.
      if (vars?.eventId) qc.invalidateQueries({ queryKey: ['eventRsvps', vars.eventId] });
      // v31 — refresh the "Needs your response" nudge so answered events
      // drop off the list straight away.
      qc.invalidateQueries({ queryKey: ['pendingResponses', clubSlug] });
    },
    // B-26 — surface capacity rejections (409 "event is full") and any other
    // RSVP failure instead of silently doing nothing.
    onError: (err, vars) => {
      alert(err?.response?.data?.error || 'Could not save your response — please try again.');
      if (vars?.eventId) qc.invalidateQueries({ queryKey: ['eventRsvps', vars.eventId] });
      qc.invalidateQueries({ queryKey: ['events', clubSlug] });
    }
  });

  // v31/B-28 — manual "remind non-responders" push (admin/TM/coach only; the
  // server enforces the role check and a 4-hour rate limit per event).
  const remindMutation = useMutation({
    mutationFn: (eventId) => axios.post(`/clubs/${clubSlug}/events/${eventId}/rsvp-reminder`).then(r => r.data),
    onSuccess: (data) => {
      alert(data.reminded > 0
        ? `Reminder sent to ${data.reminded} ${data.reminded === 1 ? 'person' : 'people'}.`
        : 'Everyone has already responded — no reminder needed.');
    },
    onError: (err) => {
      alert(err?.response?.data?.error || 'Could not send the reminder — please try again.');
    },
  });

  const addAnnMutation = useMutation({
    mutationFn: () => axios.post(`/clubs/${clubSlug}/announcements`, {
      ...newAnnouncement, teamId: newAnnouncement.teamId || null
    }),
    onSuccess: ({ data }) => { setShowNewAnn(false); setNewAnnouncement({ title: '', body: '', category: 'general', teamId: '', isPinned: false }); setJustPosted(data?.id ? data : null); qc.invalidateQueries({ queryKey: ['announcements', clubSlug] }); qc.invalidateQueries({ queryKey: ['setup-status', clubSlug] }); /* F2-1 */ },
    onError: (err) => alert(err?.response?.data?.error || 'Could not post that — please try again.'),
  });

  const deleteAnnMutation = useMutation({
    mutationFn: (id) => axios.delete(`/clubs/${clubSlug}/announcements/${id}`),
    onSuccess: (_res, id) => {
      setJustPosted(p => (p && String(p.id) === String(id) ? null : p));
      qc.invalidateQueries({ queryKey: ['announcements', clubSlug] });
    },
    onError: (err) => alert(err?.response?.data?.error || 'Could not delete that post — please try again.'),
  });

  // UC-B2 QA: Pin / Unpin and Delete show only on posts this person may
  // manage — the server's rule (canManageEventTeam): admins everything, team
  // managers their own teams' posts, club-wide posts with canClubWide.
  const canManagePost = (ann) => canPost && (clubAdmin
    || (ann.team_id ? manageableTeams.some(t => String(t.id) === String(ann.team_id)) : canClubWide));

  // UC-B2 (20 Oct): Pin / Unpin an existing post — the one edit the hub
  // offers on news. Same people as Post (admin / team manager). Edits never
  // send a push; only a new post does.
  const pinAnnMutation = useMutation({
    mutationFn: ({ id, isPinned }) => axios.patch(`/clubs/${clubSlug}/announcements/${id}`, { isPinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements', clubSlug] }),
    onError: (err) => alert(err?.response?.data?.error || 'Could not change the pin — please try again.'),
  });

  const resetEventForm = () => setNewEvent({ title: '', description: '', eventType: 'training', location: '', startsAt: '', endsAt: '', isHome: null, opponent: '', opponentClubId: null, selectedOpponent: null, venueLat: null, venueLng: null, teamName: '', teamId: '', titleDirty: false, locationDirty: false, recurType: 'none', recurUntil: '', isTicketed: false, ticketsUrl: '' });

  // Save-time geocode picker state (kept for backward compat — no longer
  // primary path; the inline venue search below is preferred).
  const [venueCandidates, setVenueCandidates] = useState([]);
  const [pendingEventPayload, setPendingEventPayload] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  // B-81: "Limit players" ON with no number used to save silently as no
  // limit. Save is now blocked with an inline message; it shows only after a
  // blocked save and goes as soon as the number is valid or the limit is off.
  const [capErrorShown, setCapErrorShown] = useState(false);
  const capInputRef = useRef(null);
  // Fresh form (opened, cancelled, saved, or a different event) → no message.
  useEffect(() => { setCapErrorShown(false); }, [showNewEvent, editingEventId]);
  // What's wrong with the player limit, or null. An event that already has a
  // cap above 999 (the API takes any whole number) keeps it unchanged.
  const capProblem = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return 'Enter how many players, or switch the limit off.';
    const stored = editingEventId ? events.find(e => e.id === editingEventId)?.max_participants : null;
    if (stored != null && s === String(stored)) return null;
    return isValidPlayerCap(s) ? null : 'Enter a whole number from 1 to 999, or switch the limit off.';
  };

  // Inline venue autocomplete state — Mapbox-backed, same UX as Home Ground.
  // Used for AWAY fixtures so admins pin the opponent's ground precisely.
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState([]);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const [venueSearchOpen, setVenueSearchOpen] = useState(false);
  const venueSearchDebounceRef = React.useRef();

  const handleVenueSearch = (val) => {
    setVenueSearchQuery(val);
    clearTimeout(venueSearchDebounceRef.current);
    if (val.trim().length < 3) {
      setVenueSearchResults([]);
      return;
    }
    setVenueSearchLoading(true);
    venueSearchDebounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get('/geocode', { params: { q: val.trim(), limit: 6 } });
        setVenueSearchResults(r.data || []);
        setVenueSearchOpen(true);
      } catch (_) { /* silent */ }
      finally { setVenueSearchLoading(false); }
    }, 300);
  };

  const handlePickVenueFromSearch = (c) => {
    setNewEvent(p => ({
      ...p,
      location: c.displayName || c.name || p.location,
      venueLat: c.lat,
      venueLng: c.lng,
      locationDirty: true,
    }));
    setVenueSearchQuery('');
    setVenueSearchResults([]);
    setVenueSearchOpen(false);
  };

  const handleClearVenue = () => {
    setNewEvent(p => ({ ...p, venueLat: null, venueLng: null, locationDirty: false }));
    setVenueSearchQuery('');
    setVenueSearchResults([]);
  };

  // When editing a series occurrence, the user is asked whether the edit
  // applies only to this occurrence (default) or to this + all future. We
  // store the choice transiently here so addEventMutation can pick the
  // right endpoint without it being part of the form schema.
  const [editScope, setEditScope] = useState('occurrence'); // 'occurrence' | 'series'

  // toIsoUtc now lives in lib/datetime.js (imported at the top) so the bulk
  // wizards share the exact same datetime-local → ISO-UTC rule. Slice 3.

  // Bulk wizards (slice 3): "Add many" fixtures + "Set up the season" training.
  const [showBulkFixtures, setShowBulkFixtures] = useState(false);
  const [showFixtureFinder, setShowFixtureFinder] = useState(false); // B-43.1
  const [showSeasonTraining, setShowSeasonTraining] = useState(false);

  // Auto-fill the end time from the start so the user doesn't pick it from
  // scratch. Duration by type/day: fixtures and weekend training = 2h;
  // midweek (Mon–Fri) training = 1h; anything else defaults to 2h. Returns a
  // datetime-local string ("YYYY-MM-DDTHH:MM") in the browser's local zone to
  // match the inputs. The user can still override the end afterwards.
  const autoEndFromStart = (startLocal, eventType) => {
    if (!startLocal) return '';
    const d = new Date(startLocal);
    if (Number.isNaN(d.getTime())) return '';
    const isWeekend = d.getDay() === 0 || d.getDay() === 6; // Sun=0, Sat=6
    const hours = eventType === 'training' ? (isWeekend ? 2 : 1) : 2;
    d.setHours(d.getHours() + hours);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // v31 — default meet time to 30 min before kick-off (Stuart, 24 Aug).
  // Returns an HH:MM string in local time, or '' if it would land on the
  // previous day (kick-offs just after midnight — not a real fixture case,
  // but don't produce a "meet time" that's after kick-off).
  const autoMeetFromStart = (startLocal) => {
    if (!startLocal) return '';
    const start = new Date(startLocal);
    if (Number.isNaN(start.getTime())) return '';
    const d = new Date(start.getTime() - 30 * 60 * 1000);
    if (d.getDate() !== start.getDate()) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // v31 — default the RSVP deadline to the club's configured offset (Club
  // Settings → rsvp_deadline_days, default 3, 0 = off) before the event.
  // Clamped to today when the event is closer than the offset; '' when the
  // event is already past or the feature is off. Editable per event.
  const autoRsvpByFromStart = (startLocal) => {
    const days = Number(club?.rsvp_deadline_days ?? 3);
    if (!startLocal || !Number.isFinite(days) || days <= 0) return '';
    const start = new Date(startLocal);
    if (Number.isNaN(start.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start < today) return '';
    let d = new Date(start.getTime() - days * 24 * 60 * 60 * 1000);
    if (d < today) d = today;
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Age-group team names show WITHOUT the trailing "s" in composed fixture
  // titles (U11s → U11, U6s → U6). Only the "U<number>s" pattern is touched,
  // so names like "Veterans" or "Senior Men's XV" are left alone.
  const teamTitleLabel = (name) => (name || '').replace(/\bU(\d+)s\b/gi, 'U$1');

  const addEventMutation = useMutation({
    // Accepts an optional pre-built payload (from handleCreateEvent which
    // first runs opponent create-or-match + geocode). Falls back to building
    // from newEvent if called without args.
    mutationFn: (payloadArg) => {
      const payload = payloadArg || { ...newEvent, teamId: newEvent.teamId || null };
      if (payload.teamId && !payload.teamName) {
        const team = teams.find(t => t.id === payload.teamId);
        if (team) payload.teamName = team.name;
      }
      // v31 — meet time + respond-by deadline. meetTime is an HH:MM anchored
      // to the start DATE (compute from the local datetime-local string,
      // BEFORE startsAt is converted to UTC — slicing the date off a UTC ISO
      // string can land on the wrong day in BST). rsvpBy is a date, stored as
      // 23:59 local that day. On edit, cleared inputs send null to clear the
      // DB columns; on create, empty fields are simply omitted.
      if (payload.startsAt && payload.meetTime) {
        payload.meetAt = toIsoUtc(`${payload.startsAt.slice(0, 10)}T${payload.meetTime}`);
      } else if (editingEventId) {
        payload.meetAt = null;
      }
      if (payload.rsvpBy) {
        payload.rsvpBy = toIsoUtc(`${payload.rsvpBy}T23:59`);
      } else if (editingEventId) {
        payload.rsvpBy = null;
      } else {
        delete payload.rsvpBy;
      }
      delete payload.meetTime;
      delete payload.meetTimeDirty;
      delete payload.rsvpByDirty;

      // B-26 — participant cap: only sent when the toggle is ON with a valid
      // number; toggling OFF (or clearing) on edit nulls the stored cap.
      if (payload.capEnabled && payload.maxParticipants) {
        payload.maxParticipants = parseInt(payload.maxParticipants, 10) || null;
      } else if (editingEventId) {
        payload.maxParticipants = null;
      } else {
        delete payload.maxParticipants;
      }
      delete payload.capEnabled;

      // Normalise times to ISO-UTC so the DB stores the moment the user
      // actually meant, not the same digits in UTC.
      if (payload.startsAt) payload.startsAt = toIsoUtc(payload.startsAt);
      if (payload.endsAt)   payload.endsAt   = toIsoUtc(payload.endsAt);

      // Translate the form's recurrence selector into the API shape. Strip
      // the UI-only fields before they hit the wire.
      if (!editingEventId && payload.recurType && payload.recurType !== 'none' && payload.recurUntil) {
        payload.recurrence = { type: payload.recurType, until: payload.recurUntil };
      }
      delete payload.recurType;
      delete payload.recurUntil;
      delete payload.seriesId;
      delete payload.selectedOpponent;
      delete payload.titleDirty;
      delete payload.locationDirty;

      if (editingEventId) {
        // Series-scope edit: PATCH the series from this occurrence forward.
        if (editScope === 'series' && newEvent.seriesId) {
          return axios.patch(
            `/clubs/${clubSlug}/events/series/${newEvent.seriesId}`,
            payload,
            { params: { fromEventId: editingEventId } }
          );
        }
        return axios.patch(`/clubs/${clubSlug}/events/${editingEventId}`, payload);
      }
      return axios.post(`/clubs/${clubSlug}/events`, payload);
    },
    onSuccess: () => {
      if (editReturnRef.current) { setSectionView(editReturnRef.current); editReturnRef.current = null; }
      setShowNewEvent(false); setEditingEventId(null); resetEventForm();
      setVenueCandidates([]); setPendingEventPayload(null);
      setEditScope('occurrence');
      qc.invalidateQueries({ queryKey: ['events', clubSlug] });
      qc.invalidateQueries({ queryKey: ['setup-status', clubSlug] }); // F2-1
    }
  });

  // Wraps the save with: (1) create-or-match opponent if free-text (server
  // auto-geocodes); (2) geocode the venue if we still have no coords;
  // (3) if 2-3 candidates, show a picker. Anything failing here is non-fatal
  // — the event still saves, just without map/weather on its card.
  const handleCreateEvent = async ({ overrideCoords, skipGeocode } = {}) => {
    if (savingEvent) return;
    if (newEvent.capEnabled && capProblem(newEvent.maxParticipants)) {
      setCapErrorShown(true);
      capInputRef.current?.focus();
      return;
    }
    setCapErrorShown(false);
    // Fixtures + training are always team-scoped — only news + events can be
    // club-wide. Guard here so it covers both create and edit in one place.
    if ((newEvent.eventType === 'match' || newEvent.eventType === 'training') && !newEvent.teamId) {
      alert('Please choose a team — fixtures and training are always for a specific team.');
      return;
    }
    // v31/B-30 — coaches can't post club-wide entries; pick one of their teams.
    if (!newEvent.teamId && !canClubWide) {
      alert('Please choose a team — club-wide events can only be posted by club admins and team managers.');
      return;
    }
    setSavingEvent(true);
    try {
      let payload = { ...newEvent, teamId: newEvent.teamId || null };
      if (overrideCoords) {
        payload.venueLat = overrideCoords.lat;
        payload.venueLng = overrideCoords.lng;
      }

      // (0) Self-heal for events that have opponentClubId already set but
      // missing venue (rows created by the old form before autofill ordering
      // was fixed). Use the joined opponent fields from the GET /events
      // response — they ride along on the event row as opponent_latitude etc.
      if (payload.opponentClubId && payload.venueLat == null && payload.isHome === false) {
        // We may already have the joined opp data on the event being edited.
        // If not (creating fresh), the typeahead's selectedOpponent has it.
        const opp = newEvent.selectedOpponent || (editingEventId && events.find(e => e.id === editingEventId)
          ? {
              name: events.find(e => e.id === editingEventId).opponent_club_name,
              short_name: events.find(e => e.id === editingEventId).opponent_short_name,
              ground_name: events.find(e => e.id === editingEventId).opponent_ground_name,
              address: events.find(e => e.id === editingEventId).opponent_address,
              latitude: events.find(e => e.id === editingEventId).opponent_latitude,
              longitude: events.find(e => e.id === editingEventId).opponent_longitude,
            }
          : null);
        if (opp && opp.latitude != null) {
          payload.venueLat = Number(opp.latitude);
          payload.venueLng = Number(opp.longitude);
        }
        if (opp && !payload.location && (opp.ground_name || opp.address)) {
          payload.location = opp.ground_name ? `${opp.name} — ${opp.ground_name}` : opp.address;
        }
      }

      // (1) Opponent create-or-match — server geocodes new opponents.
      // Also fills in location text + title if the opp has them and the
      // user typed a free-text name (so the typeahead's autofill never ran).
      if (payload.opponent && !payload.opponentClubId) {
        try {
          const oppRes = await axios.post(`/clubs/${clubSlug}/opponents`, { name: payload.opponent });
          if (oppRes.data?.id) {
            const opp = oppRes.data;
            payload.opponentClubId = opp.id;
            if (payload.venueLat == null && opp.latitude) {
              payload.venueLat = Number(opp.latitude);
              payload.venueLng = Number(opp.longitude);
            }
            // Fill location text for away fixtures only — home fixtures
            // should keep the club's home ground.
            if (!payload.location && payload.isHome === false) {
              payload.location = opp.ground_name
                ? `${opp.name} — ${opp.ground_name}`
                : (opp.address || opp.name);
            }
            // Fill title if blank
            if (!payload.title) {
              const lbl = opp.short_name || opp.name;
              payload.title = payload.isHome === true ? `vs ${lbl}` : `${lbl} Away`;
            }
          }
        } catch (_) { /* non-fatal */ }
      }

      // (1b) Auto-compose the fixture title on CREATE from team + opponent +
      // home/away (e.g. "U11s Taunton Away"). No manual Title field at create;
      // it becomes editable after the event exists.
      if (!editingEventId && payload.eventType === 'match') {
        const teamLabel = teamTitleLabel(payload.teamName || teams.find(t => t.id === payload.teamId)?.name || '');
        const oppLabel = newEvent.selectedOpponent?.short_name || newEvent.selectedOpponent?.name || payload.opponent || 'TBC';
        const haLabel = payload.isHome === false ? 'Away' : payload.isHome === true ? 'Home' : '';
        payload.title = [teamLabel, oppLabel, haLabel].filter(Boolean).join(' ');
      } else if (!editingEventId && payload.eventType === 'training') {
        // Auto-compose training titles: weekday → "U11 Midweek Training",
        // weekend → "U11 Training". Day taken from the local start value.
        const teamLabel = teamTitleLabel(payload.teamName || teams.find(t => t.id === payload.teamId)?.name || '');
        const d = newEvent.startsAt ? new Date(newEvent.startsAt) : null;
        const isWeekend = !!d && !Number.isNaN(d.getTime()) && (d.getDay() === 0 || d.getDay() === 6);
        const label = isWeekend ? 'Training' : 'Midweek Training';
        payload.title = [teamLabel, label].filter(Boolean).join(' ') || label;
      }

      // (2) Direct geocode of the location text if still no coords.
      if (payload.venueLat == null && payload.location && !skipGeocode) {
        try {
          const r = await axios.get('/geocode', { params: { q: payload.location, limit: 3 } });
          const results = r.data || [];
          if (results.length === 1) {
            payload.venueLat = results[0].lat;
            payload.venueLng = results[0].lng;
          } else if (results.length > 1) {
            // Defer — surface the picker.
            setPendingEventPayload(payload);
            setVenueCandidates(results);
            setSavingEvent(false);
            return;
          }
        } catch (_) { /* non-fatal */ }
      }

      // If admin has just picked an away-venue via the inline search AND
      // the opponent's record doesn't yet have coords, backfill the opp
      // row so the next fixture against the same opponent is auto-filled.
      // Fire-and-forget — non-fatal if it errors.
      if (
        payload.isHome === false &&
        payload.opponent && payload.opponentClubId &&
        payload.venueLat && payload.venueLng &&
        !newEvent.selectedOpponent?.latitude
      ) {
        axios.post(`/clubs/${clubSlug}/opponents`, {
          name: payload.opponent,
          latitude: payload.venueLat,
          longitude: payload.venueLng,
          address: payload.location,
        }).catch(() => {});
      }

      addEventMutation.mutate(payload);
    } catch (err) {
      alert('Failed to save event');
    } finally { setSavingEvent(false); }
  };

  // Candidate-picker actions
  const handlePickVenueCandidate = (c) => {
    if (!pendingEventPayload) return;
    const finalPayload = { ...pendingEventPayload, venueLat: c.lat, venueLng: c.lng };
    addEventMutation.mutate(finalPayload);
  };
  const handleSkipVenueCandidate = () => {
    if (!pendingEventPayload) return;
    addEventMutation.mutate(pendingEventPayload); // save without coords
  };

  // ── Shared form blocks: Venue picker + Recurrence ─────────────
  // The fixture form has its own bespoke venue UX (it's wired into Home/Away
  // and opponent autofill). This helper renders the same Nominatim-backed
  // picker for training + social forms, where it's a standalone choice.
  const renderVenuePicker = ({ placeholder, helperText }) => (
    <div className="form-group" style={{ marginTop: 12, position: 'relative' }}>
      <label className="label">Location <span className="optional">(optional)</span></label>
      {newEvent.venueLat && newEvent.venueLng ? (
        <div style={{
          padding: '10px 12px',
          background: 'rgba(30, 74, 53, 0.08)',
          borderLeft: '3px solid var(--marakin-accent, #1E4A35)',
          borderRadius: 6,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 8,
        }}>
          <div>
            <p style={{ fontSize: 14, color: 'var(--marakin-accent, #1E4A35)', fontWeight: 600, marginBottom: 2 }}>
              ✓ Venue pinned
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {newEvent.location || 'Map will show on the event card'}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleClearVenue}
            style={{ flexShrink: 0 }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            className="input"
            placeholder={placeholder || 'Search venue — name, postcode, or address'}
            value={venueSearchQuery || newEvent.location}
            onChange={e => {
              setNewEvent(p => ({ ...p, location: e.target.value, locationDirty: true }));
              handleVenueSearch(e.target.value);
            }}
            onFocus={() => venueSearchResults.length > 0 && setVenueSearchOpen(true)}
            onBlur={() => setTimeout(() => setVenueSearchOpen(false), 150)}
            autoComplete="off"
          />
          {venueSearchLoading && (
            <span style={{ position: 'absolute', right: 14, top: 38, fontSize: 13, color: 'var(--text-muted, #6B7280)' }}>
              Searching…
            </span>
          )}
          {venueSearchOpen && venueSearchResults.length > 0 && (
            <div style={{
              marginTop: 4,
              background: '#fff',
              border: '1px solid var(--border, #E5E7EB)',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              maxHeight: 280,
              overflowY: 'auto',
            }}>
              {venueSearchResults.map((c, i) => {
                const isPOI = c.kind === 'poi';
                const isPostcodeArea = c.kind === 'postcode_area';
                return (
                  <button
                    key={i}
                    type="button"
                    onMouseDown={() => handlePickVenueFromSearch(c)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: i < venueSearchResults.length - 1 ? '1px solid #F3F4F6' : 'none',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      whiteSpace: 'normal',
                    }}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2, #F4EFE6)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                        background: isPOI ? 'var(--marakin-accent, #1E4A35)' : (isPostcodeArea ? '#9CA3AF' : '#6B7280'),
                        color: '#fff', letterSpacing: 0.5,
                      }}>
                        {isPOI ? 'VENUE' : (isPostcodeArea ? 'POSTCODE' : 'ADDRESS')}
                      </span>
                      {c.name && c.name !== c.displayName && (
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                      )}
                    </div>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>📍 {c.displayName}</span>
                  </button>
                );
              })}
            </div>
          )}
          <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
            {helperText || 'Pick the address from the suggestions so the map pins precisely.'}
          </span>
        </>
      )}
    </div>
  );

  // Recurrence block — only shown on create (not edit). Editing a recurring
  // occurrence uses the series controls in the ⋮ menu instead.
  //
  // Three cadences: weekly / fortnightly / monthly. Picking any cadence
  // (i.e. anything other than 'None') auto-defaults the Until date to a
  // sensible horizon so the user never accidentally creates a "series" of
  // one. Live preview underneath shows the count + range so the recurrence
  // can't be missed.
  //
  // Monthly clamps to month-end (31 Jan → 28 Feb in non-leap years) so
  // recurrences that land on a non-existent day-of-month don't get skipped.
  const advanceMonthClient = (d) => {
    const next = new Date(d.getTime());
    const targetMonth = next.getMonth() + 1;
    const day = next.getDate();
    next.setDate(1);
    next.setMonth(targetMonth);
    const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    next.setDate(Math.min(day, lastDay));
    return next;
  };

  const recurStepDays = { weekly: 7, fortnightly: 14, monthly: null };
  const recurDefaultHorizons = { weekly: 7 * 12, fortnightly: 14 * 12, monthly: null }; // ~3 months, ~6 months, see below

  const computeRecurPreview = () => {
    const { recurType, startsAt, recurUntil } = newEvent;
    if (!recurType || recurType === 'none' || !startsAt || !recurUntil) return null;
    const start = new Date(startsAt);
    const until = new Date(`${recurUntil}T23:59:59`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(until.getTime())) return null;
    let count = 0;
    let last = start;
    let cur = new Date(start.getTime());
    while (cur.getTime() <= until.getTime() && count < 60) {
      count++;
      last = new Date(cur.getTime());
      if (recurType === 'monthly') {
        cur = advanceMonthClient(cur);
      } else {
        cur = new Date(cur.getTime() + recurStepDays[recurType] * 24 * 60 * 60 * 1000);
      }
    }
    const cadenceLabel = recurType === 'weekly' ? 'weekly' : (recurType === 'fortnightly' ? 'fortnightly' : 'monthly');
    return { count, last, cadenceLabel };
  };
  const recurPreview = computeRecurPreview();

  // Min Until date varies by cadence — never allow a series that's a no-op.
  const minRecurUntil = (() => {
    if (!newEvent.startsAt || !newEvent.recurType || newEvent.recurType === 'none') return '';
    const d = new Date(newEvent.startsAt);
    if (Number.isNaN(d.getTime())) return '';
    if (newEvent.recurType === 'monthly') {
      const next = advanceMonthClient(d);
      const pad = n => String(n).padStart(2, '0');
      return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
    }
    d.setDate(d.getDate() + recurStepDays[newEvent.recurType]);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();

  const handleRecurTypeChange = (newType) => {
    setNewEvent(p => {
      if (newType === 'none') return { ...p, recurType: 'none' };
      // Auto-pick a sensible horizon so the user doesn't have to think about
      // the Until date. Half-season-ish defaults:
      //   weekly      → ~12 weeks (a UK term)
      //   fortnightly → ~12 fortnights / ~6 months (covers a half-season of alt-Sundays)
      //   monthly     → ~6 months (covers a typical "monthly committee" cycle)
      let recurUntil = p.recurUntil;
      if (p.startsAt) {
        const d = new Date(p.startsAt);
        if (!Number.isNaN(d.getTime())) {
          if (newType === 'weekly')      d.setDate(d.getDate() + 7 * 12);
          else if (newType === 'fortnightly') d.setDate(d.getDate() + 14 * 12);
          else if (newType === 'monthly')      d.setMonth(d.getMonth() + 6);
          const pad = n => String(n).padStart(2, '0');
          recurUntil = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        }
      }
      return { ...p, recurType: newType, recurUntil };
    });
  };

  const renderRecurrenceBlock = () => {
    if (editingEventId) return null; // editing — series UX lives in the ⋮ menu
    const startMissing = !newEvent.startsAt;
    const isRecurring = newEvent.recurType && newEvent.recurType !== 'none';
    return (
      <div className="form-group" style={{ marginTop: 12 }}>
        <label className="label">Repeats</label>
        <select
          className="input"
          value={newEvent.recurType || 'none'}
          disabled={startMissing}
          onChange={e => handleRecurTypeChange(e.target.value)}
          style={{ maxWidth: 260 }}
        >
          <option value="none">Doesn't repeat</option>
          <option value="weekly">Every week</option>
          <option value="fortnightly">Every 2 weeks</option>
          <option value="monthly">Every month</option>
        </select>
        {startMissing && (
          <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
            Set a start time first to enable repeats.
          </span>
        )}
        {isRecurring && !startMissing && (
          <div style={{ marginTop: 8, padding: 10, background: 'var(--surface-2, #F4EFE6)', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Until</label>
              <input
                className="input"
                type="date"
                min={minRecurUntil}
                value={newEvent.recurUntil}
                onChange={e => setNewEvent(p => ({ ...p, recurUntil: e.target.value }))}
                style={{ maxWidth: 200 }}
              />
            </div>
            {recurPreview && recurPreview.count > 0 ? (
              <p className="form-hint" style={{ marginTop: 8, color: 'var(--marakin-accent, #1E4A35)', fontWeight: 500 }}>
                ✓ Will create <strong>{recurPreview.count}</strong> {recurPreview.cadenceLabel} occurrence{recurPreview.count === 1 ? '' : 's'}
                {' — '}
                {format(new Date(newEvent.startsAt), 'd MMM')} → {format(recurPreview.last, 'd MMM')}
                {recurPreview.count >= 60 && ' (capped at 60)'}
              </p>
            ) : (
              <p className="form-hint" style={{ marginTop: 6, color: 'var(--text-muted, #6B7280)' }}>
                Pick an end date — we'll create occurrences up to and including it.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // v31 — "Respond by" deadline block, shared by the training + events forms
  // (the fixture form has its own inline 2-col row with Meet time). Hidden
  // while a recurrence is selected: the backend deliberately skips rsvp_by on
  // series (it's date-anchored to one occurrence).
  // WT-21: only admins can open Club Settings, so only they are pointed
  // there. WT-28: "1 day", not "1 days".
  const respondByHint = `Auto-set ${plural(club?.rsvp_deadline_days ?? 3, 'day')} before${clubAdmin ? ' (change in Club Settings)' : ''} — edit or clear as needed. Non-responders get a push reminder 24h before the deadline.`;
  const renderRespondByBlock = () => {
    if (newEvent.recurType && newEvent.recurType !== 'none') return null;
    // Series-scope edits go to PATCH /events/series/:seriesId, which ignores
    // rsvpBy by design (date-anchored to one occurrence) — hide the field so
    // a filled-in deadline can't silently do nothing.
    if (editingEventId && editScope === 'series') return null;
    return (
      <div className="form-group" style={{ marginTop: 12 }}>
        <label className="label">Respond by <span className="optional">(optional)</span></label>
        <input className="input" type="date" value={newEvent.rsvpBy || ''}
          max={newEvent.startsAt ? newEvent.startsAt.slice(0, 10) : undefined}
          style={{ maxWidth: 200 }}
          onChange={e => setNewEvent(p => ({ ...p, rsvpBy: e.target.value, rsvpByDirty: true }))} />
        <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
          {respondByHint}
        </span>
      </div>
    );
  };

  // B-26 — player limit toggle (ticket #10; simplified by Stuart 24 Aug).
  // Off by default. When ON the cap applies to PLAYERS only — coaches and
  // team managers neither count towards it nor get blocked by it.
  const renderCapacityBlock = () => {
    const capNoun = 'players';
    const on = !!newEvent.capEnabled;
    const capMessage = on && capErrorShown ? capProblem(newEvent.maxParticipants) : null;
    const capInvalid = !!capMessage;
    return (
      <div className="form-group" style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={() => { setCapErrorShown(false); setNewEvent(p => ({ ...p, capEnabled: !p.capEnabled })); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
            fontWeight: 500, fontSize: 14,
            border: `1px solid ${on ? '#C4943A' : 'var(--border, #d8d2c4)'}`,
            background: on ? 'rgba(196,148,58,0.14)' : 'var(--surface-2, #F4EFE6)',
            color: 'var(--text-primary, #2C2B28)',
          }}
        >
          <span style={{
            width: 20, height: 20, borderRadius: 4, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#fff',
            border: `2px solid ${on ? '#C4943A' : 'var(--text-muted, #9a9a9a)'}`,
            background: on ? '#C4943A' : 'transparent',
          }}>{on ? '✓' : ''}</span>
          👥 Limit {capNoun}{on ? ' — ON' : ''}
        </button>
        {on && (
          <div style={{ marginTop: 8, padding: 10, background: 'var(--surface-2, #F4EFE6)', borderRadius: 6 }}>
            <label className="label">Max {capNoun}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input className="input" type="number" min={1} max={999} style={{ maxWidth: 100 }}
                placeholder="e.g. 20"
                value={newEvent.maxParticipants || ''}
                ref={capInputRef}
                aria-invalid={capInvalid || undefined}
                aria-describedby={capInvalid ? 'cap-error' : undefined}
                onChange={e => setNewEvent(p => ({ ...p, maxParticipants: e.target.value }))} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{capNoun}</span>
            </div>
            {capInvalid && (
              <p id="cap-error" className="cap-error" role="alert">
                {capMessage}
              </p>
            )}
            <p className="form-hint" style={{ marginTop: 6, color: 'var(--text-muted, #6B7280)' }}>
              Counts people attending as players — decided by the team sheet, so a club admin, coach
              or manager only takes a place if they're on the team as a player.
              Shows on the card; once full, "Going" is blocked until someone drops out.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Ticketing block (v16). Shown on fixture + event forms (NOT training).
  // A "Ticketed Event" checkbox flips the event into ticketed mode and
  // reveals a link field. Today that's an external ticket URL; this is the
  // hook for Marakin's own QR ticketing later (scan unique QR tickets on the
  // door to track sales + attendance, mirroring sponsor QR redemption).
  const renderTicketingBlock = () => (
    <div className="form-group" style={{ marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setNewEvent(p => ({ ...p, isTicketed: !p.isTicketed }))}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
          fontWeight: 500, fontSize: 14,
          border: `1px solid ${newEvent.isTicketed ? '#C4943A' : 'var(--border, #d8d2c4)'}`,
          background: newEvent.isTicketed ? 'rgba(196,148,58,0.14)' : 'var(--surface-2, #F4EFE6)',
          color: 'var(--text-primary, #2C2B28)',
        }}
      >
        <span style={{
          width: 20, height: 20, borderRadius: 4, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#fff',
          border: `2px solid ${newEvent.isTicketed ? '#C4943A' : 'var(--text-muted, #9a9a9a)'}`,
          background: newEvent.isTicketed ? '#C4943A' : 'transparent',
        }}>{newEvent.isTicketed ? '✓' : ''}</span>
        🎫 Ticketed event{newEvent.isTicketed ? ' — ON' : ''}
      </button>
      {newEvent.isTicketed && (
        <div style={{ marginTop: 8, padding: 10, background: 'var(--surface-2, #F4EFE6)', borderRadius: 6 }}>
          <label className="label">Tickets link <span className="optional">(optional for now)</span></label>
          <input
            className="input"
            type="url"
            inputMode="url"
            placeholder="e.g. eventbrite.co.uk/e/your-event"
            value={newEvent.ticketsUrl}
            onChange={e => setNewEvent(p => ({ ...p, ticketsUrl: e.target.value }))}
          />
          <p className="form-hint" style={{ marginTop: 6, color: 'var(--text-muted, #6B7280)' }}>
            Paste where members buy tickets — it shows as a “Get Tickets” button on the event.
            Leave blank to set it up later. (Marakin QR ticketing will live here in future.)
          </p>
        </div>
      )}
    </div>
  );

  const deleteEventMutation = useMutation({
    mutationFn: (eventId) => axios.delete(`/clubs/${clubSlug}/events/${eventId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events', clubSlug] })
  });

  // Series-aware delete. eventId is required (it acts as the cut-off
  // reference for "this and future" deletions). scope:
  //   'occurrence' → single event
  //   'future'     → this event and all later occurrences in the series
  //   'series'     → every event in the series, past and future
  const deleteSeriesMutation = useMutation({
    mutationFn: ({ seriesId, eventId, scope }) => {
      if (scope === 'series') {
        return axios.delete(`/clubs/${clubSlug}/events/series/${seriesId}`);
      }
      return axios.delete(`/clubs/${clubSlug}/events/series/${seriesId}`, {
        params: { fromEventId: eventId },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events', clubSlug] })
  });

  const handleEditEvent = (ev, scope = 'occurrence') => {
    editReturnRef.current = sectionView === 'whatson' ? 'whatson' : null;
    // The form sits above the Upcoming / Past list — show the one it's in.
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
    setFixtureView(ev.starts_at && new Date(ev.starts_at) < dayStart ? 'past' : 'upcoming');
    const toLocalInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      // Format for datetime-local: yyyy-MM-ddTHH:mm
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setEditScope(scope);
    setEditingEventId(ev.id);
    setNewEvent({
      title: ev.title || '',
      description: ev.description || '',
      eventType: ev.event_type || 'general',
      location: ev.location || '',
      startsAt: toLocalInput(ev.starts_at),
      endsAt: toLocalInput(ev.ends_at),
      isHome: ev.is_home,
      opponent: ev.opponent || '',
      opponentClubId: ev.opponent_club_id || null,
      venueLat: ev.venue_lat || null,
      venueLng: ev.venue_lng || null,
      teamName: ev.team_name || '',
      teamId: ev.team_id || '',
      titleDirty: true,    // editing — don't auto-overwrite
      locationDirty: true,
      // Series fields — preserved for the "edit and future" option, but the
      // recurrence-create UI itself is hidden in edit mode.
      seriesId: ev.series_id || null,
      recurType: 'none',
      recurUntil: ev.recurrence_until ? ev.recurrence_until.slice(0, 10) : '',
      // Ticketing (v16)
      isTicketed: ev.is_ticketed || false,
      ticketsUrl: ev.tickets_url || '',
      // v31 — meet time (HH:MM local) + respond-by (yyyy-MM-dd local)
      meetTime: (() => {
        if (!ev.meet_at) return '';
        const d = new Date(ev.meet_at);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      })(),
      rsvpBy: (() => {
        if (!ev.rsvp_by) return '';
        const d = new Date(ev.rsvp_by);
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      })(),
      // B-26 — participant cap round-trips as a plain number string;
      // toggle reflects whether a cap is stored
      maxParticipants: ev.max_participants || '',
      capEnabled: !!ev.max_participants,
      // Editing — never auto-overwrite whatever meet time / RSVP deadline is
      // stored (or was deliberately left empty) when the kick-off changes.
      meetTimeDirty: true,
      rsvpByDirty: true,
    });
    // Auto-switch to the right section so the form is visible
    if (ev.event_type === 'match') setSectionView('fixtures');
    else if (ev.event_type === 'training') setSectionView('training');
    else setSectionView('events');
    setShowNewEvent(true);
  };

  const handleDeleteEvent = (ev, scope = 'occurrence') => {
    if (scope === 'occurrence' || !ev.series_id) {
      if (window.confirm(`Delete "${ev.title}"? Members who have RSVP'd will lose this event from their schedule.`)) {
        deleteEventMutation.mutate(ev.id);
      }
      return;
    }
    if (scope === 'future') {
      const evDate = ev.starts_at ? format(parseISO(ev.starts_at), 'd MMM') : 'this event';
      if (window.confirm(`Delete "${ev.title}" on ${evDate} and every later occurrence in this series?`)) {
        deleteSeriesMutation.mutate({ seriesId: ev.series_id, eventId: ev.id, scope: 'future' });
      }
      return;
    }
    if (scope === 'series') {
      if (window.confirm(`Delete the entire "${ev.title}" series? Every occurrence — past and future — will be removed.`)) {
        deleteSeriesMutation.mutate({ seriesId: ev.series_id, eventId: ev.id, scope: 'series' });
      }
      return;
    }
  };

  const cancelEventForm = () => {
    if (editReturnRef.current) { setSectionView(editReturnRef.current); editReturnRef.current = null; }
    setShowNewEvent(false);
    setEditingEventId(null);
    setEditScope('occurrence'); // B-82
    resetEventForm();
  };

  // B-82 (QA): switching tab with the form open used to carry it to the new
  // tab ("Edit Training Session" holding a fixture). Close it instead.
  // handleEditEvent sets the tab itself, so it doesn't come through here.
  const selectSection = (view) => {
    editReturnRef.current = null; // they picked a tab themselves
    if (showNewEvent) cancelEventForm();
    setSectionView(view);
  };

  // B-24: the server used to return the OLDEST 100 messages, so once a thread
  // passed 100 it froze — members never saw new posts, not even their own.
  // Now it returns the newest page, and tells us how many came before it.
  const { data: chatPage } = useQuery({
    queryKey: ['eventMessages', openChatEventId],
    queryFn: () => fetchList(`/clubs/${clubSlug}/events/${openChatEventId}/messages`),
    enabled: !!openChatEventId && !!user && !!membership,   // B-54: members only (server 403s others)
    refetchInterval: 5000,
  });
  const chatMessages = chatPage?.items ?? [];
  const chatOlderCount = Math.max((chatPage?.total ?? 0) - chatMessages.length, 0);

  const { data: rsvpAttendees = { yes: [], no: [] } } = useQuery({
    queryKey: ['eventRsvps', expandedEventId],
    queryFn: () => axios.get(`/clubs/${clubSlug}/events/${expandedEventId}/rsvps`).then(r => r.data),
    enabled: !!expandedEventId && !!user && !!membership,   // B-54: members only
  });

  // v31 — "Needs your response" nudge (tickets #11 + #13): upcoming events
  // where the member (or their children) haven't said Going / Not going.
  // B-24: the server now caps whole EVENTS rather than event×person rows, so
  // every event that arrives carries its full set of people. Previously a
  // parent with three children could get a card listing two of them.
  // W-8 (Stuart, 9 Sept): card ON ICE — "clutters the top of the app". Query
  // disabled and render gated below; endpoint + code kept intact so it can
  // return in a lighter form (Matt's #13 ask — see tracker before deleting).
  const SHOW_NEEDS_RESPONSE_CARD = false;
  const { data: pendingPage } = useQuery({
    queryKey: ['pendingResponses', clubSlug],
    queryFn: () => fetchList(`/clubs/${clubSlug}/events/pending-responses`),
    enabled: SHOW_NEEDS_RESPONSE_CARD && !!user && !!membership,
  });
  const pendingResponses = pendingPage?.items ?? [];
  const pendingHasMore = pendingPage?.hasMore ?? false;

  // B-32 — co-parenting requests awaiting MY approval (only returns rows if
  // I'm a registered parent of the requested child).
  const { data: coparentRequests = [] } = useQuery({
    queryKey: ['coparentRequests', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/children/coparent-requests/pending`).then(r => r.data),
    enabled: !!user && !!membership,
  });
  const resolveCoparent = useMutation({
    mutationFn: ({ id, action }) =>
      axios.post(`/clubs/${clubSlug}/children/coparent-requests/${id}/${action}`).then(r => r.data),
    // onSettled, not onSuccess: a 410 (other parent already resolved it)
    // should also refresh the list so the stale card disappears (QA, 25 Aug).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['coparentRequests', clubSlug] });
      qc.invalidateQueries({ queryKey: ['my-children', clubSlug] });
    },
    // B-76 QA: approving links a child to someone — refused on a staff "view as".
    // QA round 2 (L1): every other refusal says why (archived requester,
    // not a member any more, the club's sponsor login). A 410 just means the
    // other parent got there first — the card refreshes away quietly.
    onError: (err) => {
      const data = err?.response?.data;
      if (data?.code === 'impersonating') alert(VIEWING_AS_NOTE);
      else if (err?.response?.status !== 410) alert(data?.error || 'Could not save that — please try again.');
    },
  });

  // B-90 — team places a parent asked for, waiting for a club admin or the
  // team's manager (the server only returns the ones THIS person can decide).
  const { data: waitingPlacesData } = useQuery({
    queryKey: ['teamPlacesWaiting', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/team-places/waiting`).then(r => r.data),
    // !! — canManage can be null (hasTeamRole's legacy arm), and TanStack v5
    // throws on a non-boolean `enabled`, crashing the hub for parents/supporters.
    enabled: !!user && !!membership && !!canManage,
  });
  const waitingPlaces = waitingPlacesData?.places || [];
  const decidePlace = useMutation({
    mutationFn: ({ id, action }) =>
      axios.post(`/clubs/${clubSlug}/team-places/${id}/${action}`).then(r => r.data),
    onSettled: () => qc.invalidateQueries({ queryKey: ['teamPlacesWaiting', clubSlug] }),
    onError: (err) => {
      if (err?.response?.data?.code === 'impersonating') alert(VIEWING_AS_NOTE);
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: (body) => axios.post(`/clubs/${clubSlug}/events/${openChatEventId}/messages`, { body }),
    onSuccess: () => { setChatMessage(''); qc.invalidateQueries({ queryKey: ['eventMessages', openChatEventId] }); },
    // B-54: a refused message used to vanish with no error. Say why, keep the text.
    onError: (err) => {
      alert(err?.response?.data?.error || 'Could not send your message — please try again.');
    },
  });

  // WT-18: the "Support the club" bar needs a live sponsor offer to point at.
  // Same query (key + fetch) as Sponsor Offers and the Wallet's browse list,
  // so a list already in the cache is used rather than asked for again.
  const { data: liveOffers = [] } = useQuery({
    queryKey: ['sponsors', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors`).then(r => r.data),
    enabled: !!user && !!membership && !supportDismissed,
    staleTime: 5 * 60 * 1000,
  });
  // Typing anywhere (event chat, a reply note): on a phone the keyboard lifts
  // the bar straight over the box being typed in.
  const [typingInField, setTypingInField] = useState(false);
  useEffect(() => {
    const isTextField = (el) => !!el && (el.tagName === 'TEXTAREA' || el.isContentEditable
      || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(el.type)));
    // Read it once the focus has settled, so hopping between fields doesn't flash the bar.
    const update = () => setTimeout(() => setTypingInField(isTextField(document.activeElement)), 0);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);
  const formOrPopupOpen = showNewEvent || showNewAnn || showJoin || showBulkFixtures
    || showSeasonTraining || showFixtureFinder || venueCandidates.length > 0 || typingInField;
  const barEligible = !!club && !!user && !!membership && !supportDismissed
    && Array.isArray(liveOffers) && liveOffers.length > 0;
  const showSupportBar = barEligible && !formOrPopupOpen;
  // B-86 (Stuart, 18 Oct): a sponsor the club marks Featured, with a logo,
  // takes over the bar as its ad; tapping opens its offer (and QR) in the
  // Wallet. The list is already ordered featured-first by the server. A
  // stop-gap ahead of B-50's proper banners: no new data, no tracking yet.
  const featuredSponsor = Array.isArray(liveOffers)
    ? liveOffers.find(s => s.featured && s.logo_url && s.offer_title) : null;
  // The page keeps exactly the bar's height free below the last card (its
  // text wraps to more lines on a phone), measured while it's on screen.
  // QA: the space stays while the bar is only stepping aside (a form, a box
  // being typed in) — dropping it made the focused box jump 85px as the
  // phone keyboard opened. So the last measurement is kept on unmount.
  const [supportBarHeight, setSupportBarHeight] = useState(0);
  const supportBarObserver = useRef(null);
  const supportBarRef = useCallback((el) => {
    supportBarObserver.current?.disconnect();
    supportBarObserver.current = null;
    if (!el) return;
    const measure = () => setSupportBarHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      supportBarObserver.current = new ResizeObserver(measure);
      supportBarObserver.current.observe(el);
    }
  }, []);

  if (clubLoading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  if (!club) return <div className="page-loading"><p>Club not found</p></div>;

  const accentColor = club.accent_color || '#C4943A';
  // Hero location → opens the device's maps app, same as the event-card maps.
  const heroGroundLabel = club.home_ground_address ? homeGroundLabel(club) : club.location; // WT-21
  const heroMapsUrl = (club.home_ground_lat != null && club.home_ground_lng != null)
    ? `https://www.google.com/maps/search/?api=1&query=${Number(club.home_ground_lat)},${Number(club.home_ground_lng)}`
    : (heroGroundLabel ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(heroGroundLabel)}` : null);
  // fixtureEvents / trainingEvents / otherEvents now come straight from
  // their own paginated queries above (F-009) — no client-side splitting.

  // The "next upcoming" event in each section — used to gate the LeaveByPill
  // so we don't fire OSRM requests for every card. Picks the soonest future
  // event with venue coords (no point computing drive time if we don't have
  // somewhere to drive to). Only meaningful when the section is in 'upcoming'
  // view — past events have no leave-by relevance.
  const nextUpcomingId = (list) => {
    if (fixtureView === 'past') return null;
    const nowMs = Date.now();
    const next = list
      .filter(ev => ev.venue_lat != null && ev.venue_lng != null)
      .filter(ev => new Date(ev.starts_at).getTime() >= nowMs - 60 * 60 * 1000) // 1h grace
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())[0];
    return next ? next.id : null;
  };
  const nextFixtureId = nextUpcomingId(fixtureEvents);
  const nextTrainingId = nextUpcomingId(trainingEvents);
  const nextOtherId = nextUpcomingId(otherEvents);
  // What's on is always upcoming, so not tied to the tabs' Upcoming / Past.
  const nextWhatsOnId = (() => {
    const nowMs = Date.now();
    const next = whatsOnEvents.find(ev => ev.venue_lat != null && ev.venue_lng != null
      && new Date(ev.starts_at).getTime() >= nowMs - 60 * 60 * 1000);
    return next ? next.id : null;
  })();

  // Sponsors don't use the general club hub — send them to their scan page.
  // MUST be after all hooks (Rules of Hooks): an early return above the hook
  // declarations crashed with "rendered fewer hooks than expected" when a
  // sponsor opened a hub.
  if (membership?.role === 'sponsor') return <Navigate to={`/${clubSlug}/scan`} replace />;

  return (
    <div className="club-hub" style={{ '--accent': accentColor }}>
      <Navbar club={club} />
      {/* Role-aware guided tour — auto-starts on first visit, relaunches from
          the navbar account menu ("Take the tour"). Slice 2. */}
      <GuidedTour club={club} surface="hub" />

      <PageHeader
        icon={Home}
        title="Club Hub"
        subtitle={<>{club.name}{club.nickname ? ` · ${club.nickname}` : ''}</>}
      >
        {teams.length > 0 && (
          <select
            className="team-filter-select"
            value={selectedTeamFilter}
            onChange={e => { filterTouched.current = true; setSelectedTeamFilter(e.target.value); }}
            aria-label="Filter by team"
            data-tour="hub-team-filter"
          >
            <option value="all">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </PageHeader>
      {/* W-2: unverified + unsubmitted → the revenue case, one tap from the form. */}
      {clubAdmin && !club.verified_at && !club.verification_submitted_at && !verifyBannerDismissed && (
        <div className="hub-verify-banner" data-tour="hub-verify-banner">
          <TrendingUp size={18} />
          <div className="hub-verify-banner-text">
            <strong>Unlock sponsor revenue for {club.name}.</strong>
            <span>Marakin brings sponsor money into verified clubs. Two minutes of details and we'll switch your sponsorship tools on.</span>
          </div>
          <Link className="btn btn-primary btn-sm" to={`/${clubSlug}/admin?tab=sponsors`}>Complete verification</Link>
          <button type="button" className="hub-verify-banner-close" aria-label="Dismiss for now" onClick={dismissVerifyBanner}><X size={15} /></button>
        </div>
      )}
      {/* F2-1: club admins see this until the club is set up, then never again.
          Not Marakin staff, whose admin hat on every club is implicit. */}
      {clubAdmin && !membership?.implicit && (
        <SetupChecklist club={club} clubSlug={clubSlug} onFirstPost={() => {
          // Same fresh form as the Add Fixture button; it scrolls into view.
          setSectionView('fixtures');
          setEditingEventId(null); setEditScope('occurrence');
          setNewEvent({ title: '', description: '', eventType: 'match', location: '', startsAt: '', endsAt: '', isHome: null, opponent: '', opponentClubId: null, selectedOpponent: null, venueLat: null, venueLng: null, ...onlyTeamPreset, titleDirty: false, locationDirty: false, recurType: 'none', recurUntil: '' });
          setShowNewEvent(true);
        }} />
      )}
      {/* Non-members: one clear invitation, button at the end (Stuart, 12 Oct). */}
      {/* WT-4: a waiting request gets its own state, with Withdraw. */}
      {waitingRequest && (
        <div className="hub-join-banner hub-join-banner-waiting" data-testid="hub-join-waiting">
          <Hourglass size={22} className="hub-join-banner-icon" aria-hidden="true" />
          <div className="hub-join-banner-text">
            <strong>Waiting for an admin</strong>
            <span>{joinRequestSummary(waitingRequest)} We'll let you know when an admin at {club.name} approves your request.</span>
            {withdrawError && <span className="hub-join-banner-error" role="alert">{withdrawError}</span>}
          </div>
          <button className="btn btn-secondary" disabled={withdrawMutation.isPending} onClick={withdrawJoinRequest}>
            {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw request'}
          </button>
        </div>
      )}
      {/* Signed in: only once we know there's no waiting request (no flash).
          WT-1 review: not while a stored session is still loading either. */}
      {/* WT-16: not while a members-only link notice is making the same offer. */}
      {!authLoading && (!user || (user && !membership && !joinRequestsPending)) && !waitingRequest && !memberOnlyLink && (
        <div className="hub-join-banner">
          <Users size={22} className="hub-join-banner-icon" aria-hidden="true" />
          <div className="hub-join-banner-text">
            <strong>{user ? `Part of ${club.name}?` : `Play, coach or support at ${club.name}?`}</strong>
            <span>{user
              ? "Ask to join to see fixtures and training, who's going, and chat with your team."
              : 'Create a free account, then ask to join to see fixtures and training and chat with your team.'}</span>
            {withdrawError && <span className="hub-join-banner-error" role="alert">{withdrawError}</span>}
          </div>
          <button className="btn btn-primary" onClick={() => { withdrawMutation.reset(); setShowJoin(true); }}>
            {user ? 'Request to join' : 'Join the club'}
          </button>
        </div>
      )}

      {/* ── Section toggle: What's on | Fixtures | Training | News | Events ── */}
      <div className="section-toggle-wrap">
        <div className="section-toggle" data-tour="hub-sections">
          <button
            className={`section-toggle-btn ${sectionView === 'whatson' ? 'active' : ''}`}
            data-tour="hub-whatson-tab"
            onClick={() => selectSection('whatson')}
            style={sectionView === 'whatson' ? { borderBottomColor: accentColor, color: accentColor } : {}}
          >
            <CalendarDays size={15} /> What's on
          </button>
          <button
            className={`section-toggle-btn ${sectionView === 'fixtures' ? 'active' : ''}`}
            data-tour="hub-fixtures-tab"
            onClick={() => selectSection('fixtures')}
            style={sectionView === 'fixtures' ? { borderBottomColor: accentColor, color: accentColor } : {}}
          >
            <Trophy size={15} /> Fixtures
          </button>
          <button
            className={`section-toggle-btn ${sectionView === 'training' ? 'active' : ''}`}
            data-tour="hub-training-tab"
            onClick={() => selectSection('training')}
            style={sectionView === 'training' ? { borderBottomColor: accentColor, color: accentColor } : {}}
          >
            <Dumbbell size={15} /> Training
          </button>
          <button
            className={`section-toggle-btn ${sectionView === 'news' ? 'active' : ''}`}
            data-tour="hub-news-tab"
            onClick={() => selectSection('news')}
            style={sectionView === 'news' ? { borderBottomColor: accentColor, color: accentColor } : {}}
          >
            <Megaphone size={15} /> News
            {/* B-24: the true total, not the page size — this badge used to
                stick at "20" once the cap bit. */}
            {announcementsTotal > 0 && <span className="section-count">{announcementsTotal}</span>}
          </button>
          <button
            className={`section-toggle-btn ${sectionView === 'events' ? 'active' : ''}`}
            onClick={() => selectSection('events')}
            style={sectionView === 'events' ? { borderBottomColor: accentColor, color: accentColor } : {}}
          >
            <Calendar size={15} /> Events
          </button>
        </div>
      </div>

      {/* WT-18: room under the last card for the Support bar, sized to the
          bar itself (a fixed 88px fell short when its text wraps on a phone),
          and kept while the bar is only hidden for a moment. */}
      <div className="club-content-single" style={{ paddingBottom: barEligible && supportBarHeight ? supportBarHeight + 16 : undefined }}>

        {/* ── B-32: co-parenting approval card — shown only to registered
            parents with a pending request. Confirms/declines in place. ── */}
        {user && membership && coparentRequests.length > 0 && (
          <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid #2D7A4F', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 16 }}>👨‍👩‍👧</span>
              <h3 style={{ fontSize: 15, margin: 0 }}>Co-parenting request{coparentRequests.length > 1 ? 's' : ''}</h3>
            </div>
            {coparentRequests.map(cr => (
              <div key={cr.id} style={{ padding: '8px 0 6px', borderBottom: '1px solid var(--border, #E5E0D4)' }}>
                <p style={{ margin: '0 0 8px', fontSize: 13.5 }}>
                  <strong>{cr.requesterName}</strong>
                  {cr.requesterEmail ? <span style={{ color: 'var(--text-secondary)' }}> ({cr.requesterEmail})</span> : null}
                  {cr.kind === 'merge' ? (
                    <>
                      {' '}says they're also <strong>{cr.childName}</strong>'s parent — they added
                      {' '}{cr.childName} with the same date of birth. Confirm only if you know them as
                      {' '}{cr.childName}'s other parent or guardian; we'll join the two records.
                    </>
                  ) : (
                    <>
                      {' '}has asked to co-parent <strong>{cr.childName}</strong>. Confirm only if you
                      know them as {cr.childName}'s other parent or guardian.
                    </>
                  )}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-rsvp yes"
                    style={{ minWidth: 0, padding: '4px 14px', fontSize: 12.5 }}
                    disabled={resolveCoparent.isPending}
                    onClick={() => resolveCoparent.mutate({ id: cr.id, action: 'approve' })}
                  >
                    <Check size={12} /> Confirm
                  </button>
                  <button
                    className="btn btn-rsvp no"
                    style={{ minWidth: 0, padding: '4px 14px', fontSize: 12.5 }}
                    disabled={resolveCoparent.isPending}
                    onClick={() => resolveCoparent.mutate({ id: cr.id, action: 'decline' })}
                  >
                    <X size={12} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── B-90: team places waiting for this admin / team manager ── */}
        {user && membership && waitingPlaces.length > 0 && (
          <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid #C4943A', padding: '12px 16px' }}>
            <h3 style={{ fontSize: 15, margin: '0 0 4px' }}>Waiting to confirm</h3>
            <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
              A parent added their child to your team. Until you confirm, the child isn't on the team:
              not on its roster or availability lists, and their family doesn't get its notifications.
            </p>
            {waitingPlaces.map(p => (
              <div key={p.id} style={{ padding: '8px 0 6px', borderBottom: '1px solid var(--border, #E5E0D4)' }}>
                <p style={{ margin: '0 0 8px', fontSize: 13.5 }}>
                  <strong>{p.childName}</strong> for <strong>{p.teamName}</strong>
                  {p.requestedBy ? <span style={{ color: 'var(--text-secondary)' }}> — added by {p.requestedBy}</span> : null}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-rsvp yes" style={{ minWidth: 0, padding: '4px 14px', fontSize: 12.5 }}
                    disabled={decidePlace.isPending}
                    onClick={() => decidePlace.mutate({ id: p.id, action: 'confirm' })}>
                    <Check size={12} /> Confirm
                  </button>
                  <button className="btn btn-rsvp no" style={{ minWidth: 0, padding: '4px 14px', fontSize: 12.5 }}
                    disabled={decidePlace.isPending}
                    onClick={() => decidePlace.mutate({ id: p.id, action: 'decline' })}>
                    <X size={12} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── v31: "Needs your response" nudge (tickets #11 + #13) — lists
            upcoming events the member (and their children) haven't answered,
            with one-tap Going / Not going per person. Answered events drop
            off immediately (rsvpMutation invalidates pendingResponses). ── */}
        {SHOW_NEEDS_RESPONSE_CARD && user && membership && pendingResponses.length > 0 && !nudgeDismissed && (() => {
          // Soonest deadline first (rsvp_by, falling back to the event start).
          const sorted = [...pendingResponses].sort((a, b) =>
            new Date(a.rsvpBy || a.startsAt).getTime() - new Date(b.rsvpBy || b.startsAt).getTime());
          const shown = nudgeExpanded ? sorted : sorted.slice(0, 2);
          const hidden = sorted.length - shown.length;
          return (
          <div className="card" style={{ marginBottom: 14, borderLeft: '3px solid #B8832E', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>📣</span>
              <h3 style={{ fontSize: 15, margin: 0 }}>Needs your response</h3>
              {/* B-24: the badge counts people across the events we were
                  sent; if more events qualified than the server returns,
                  say so rather than under-counting silently. */}
              <span className="section-count">
                {pendingResponses.reduce((n, e) => n + e.people.length, 0)}
                {pendingHasMore ? '+' : ''}
              </span>
              <button
                onClick={() => setNudgeDismissed(true)}
                aria-label="Hide for now"
                title="Hide for now"
                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, lineHeight: 0 }}
              >
                <X size={15} />
              </button>
            </div>
            {shown.map(pe => (
              <div key={pe.id} style={{ padding: '8px 0 4px', borderBottom: '1px solid var(--border, #E5E0D4)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 13.5 }}>{pe.title}</strong>
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {formatEventDate(pe.startsAt)}
                    {/* Skip the team chip when the title already names the team (was triple-stated) */}
                    {pe.teamName && !pe.title.includes(pe.teamName) ? ` · ${pe.teamName}` : ''}
                  </span>
                  {pe.rsvpBy && (
                    <span className="rsvp-deadline" style={{ fontSize: 12 }}>
                      by {format(new Date(pe.rsvpBy), 'EEE d MMM')}
                    </span>
                  )}
                </div>
                {pe.people.map(person => (
                  <div key={person.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, minWidth: 64 }}>
                      {person.isSelf ? 'You' : `${person.firstName} ${person.lastName}`}
                    </span>
                    <button
                      className="btn btn-rsvp yes"
                      style={{ minWidth: 0, padding: '4px 12px', fontSize: 12.5 }}
                      disabled={rsvpMutation.isPending}
                      onClick={() => rsvpMutation.mutate({ eventId: pe.id, status: 'yes', onBehalfOfUserId: person.isSelf ? null : person.id })}
                    >
                      <Check size={12} /> Going
                    </button>
                    <button
                      className="btn btn-rsvp no"
                      style={{ minWidth: 0, padding: '4px 12px', fontSize: 12.5 }}
                      disabled={rsvpMutation.isPending}
                      onClick={() => rsvpMutation.mutate({ eventId: pe.id, status: 'no', onBehalfOfUserId: person.isSelf ? null : person.id })}
                    >
                      <X size={12} /> Not going
                    </button>
                  </div>
                ))}
              </div>
            ))}
            {(hidden > 0 || nudgeExpanded) && (
              <button
                onClick={() => setNudgeExpanded(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 0', fontSize: 13, fontWeight: 700, color: '#B8832E' }}
              >
                {nudgeExpanded ? 'Show fewer' : `+ ${hidden} more`}
              </button>
            )}
          </div>
          );
        })()}

        {/* ── Floating "Support the club" bar — members only, dismissible.
            WT-18: only when the club has a live sponsor offer (it used to
            open an empty Wallet), and out of the way while a form or pop-up
            is open or someone is typing (on a laptop it sat over Add
            Fixture's Create / Cancel). The page keeps room for it below the
            last card. Dismiss still lasts this visit only (UC-B4). ── */}
        {showSupportBar && (
          <div ref={supportBarRef} data-testid="support-bar" style={{
            position: 'fixed', left: 0, right: 0,
            bottom: 'calc(46px + var(--sab))', /* B-15 */
            zIndex: 80, padding: '0 12px', pointerEvents: 'none',
          }}>
            <div style={{ maxWidth: 760, margin: '0 auto', pointerEvents: 'auto' }}>
              <div className={`card offers-promo${featuredSponsor ? ' featured-sponsor-card' : ''}`} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', boxShadow: '0 4px 18px rgba(0,0,0,0.18)',
              }}>
                {featuredSponsor ? (
                  <Link to={`/${clubSlug}/wallet?offer=${featuredSponsor.id}`} className="featured-sponsor-ad"
                    data-testid="featured-sponsor-ad" aria-label={`Sponsored: ${featuredSponsor.business_name}. ${featuredSponsor.offer_title}. Open the offer`}>
                    <img src={featuredSponsor.logo_url} alt="" className="featured-sponsor-logo" />
                    <span className="featured-sponsor-text">
                      {/* Non-breaking spaces: if it must wrap, it's "Sponsored ·" / "Tap for QR". */}
                      <span className="featured-sponsor-label">{'Sponsored · Tap for QR'}</span>
                      {/* The short offer line keeps the bar slim on a phone. */}
                      <span className="featured-sponsor-offer">{featuredSponsor.offer_value || featuredSponsor.offer_title}</span>
                    </span>
                  </Link>
                ) : (
                <Link to={`/${clubSlug}/wallet`} style={{
                  display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
                  flex: 1, minWidth: 0,
                }}>
                  <span style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: accentColor, color: '#fff',
                  }}>
                    <Gift size={18} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 800, color: 'var(--text-primary)', fontSize: 15 }}>Support the club</span>
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.35 }}>
                      Use your sponsor discounts here — you save, and the club earns every time.
                    </span>
                  </span>
                </Link>
                )}
                {/* WT-27: 40px to tap (was 26). */}
                <button type="button" className="support-bar-dismiss" onClick={() => setSupportDismissed(true)} aria-label="Dismiss">
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WT-16 (B-62): a members-only link → ONE message with one main
            action and one other, in place of the join banner (it hides while
            this shows), so a stranger isn't offered three different ways in.
            Sign in / Create account both bring them back to the link.
            WT-4: already asked → say so, no actions. */}
        {memberOnlyLink && (() => {
          const backTo = memberOnlyLink.newsId ? `/${clubSlug}?news=${memberOnlyLink.newsId}` : `/${clubSlug}?event=${memberOnlyLink.eventId}`;
          return (
            <div className="deeplink-notice member-link-notice" role="status" data-testid="member-link-notice">
              <div className="member-link-notice-text">
                <strong>{`This ${memberOnlyLink.what} is for ${club.name} members`}</strong>
                <span>{!user
                  ? 'Sign in to see it. New to Marakin? Create a free account, then ask to join the club.'
                  : waitingRequest
                    ? "You'll see it once an admin approves your request to join."
                    : 'Ask to join the club to see it. An admin will check your request.'}</span>
              </div>
              {!user ? (
                <div className="member-link-notice-actions">
                  <Link className="btn btn-primary" to="/login" state={{ from: backTo }}><LogIn size={15} /> Sign in</Link>
                  <Link className="btn btn-secondary" to="/register" state={{ from: backTo }}><UserPlus size={15} /> Create account</Link>
                </div>
              ) : !waitingRequest && (
                <div className="member-link-notice-actions">
                  <button className="btn btn-primary" onClick={() => { setMemberOnlyLink(null); withdrawMutation.reset(); setShowJoin(true); }}>Ask to join</button>
                  <button className="btn btn-ghost" onClick={() => setMemberOnlyLink(null)}>Not now</button>
                </div>
              )}
            </div>
          );
        })()}
        {deepLinkNotice && (
          <div className="deeplink-notice" role="status">
            <span>{deepLinkNotice}</span>
            <button className="btn btn-ghost btn-icon deeplink-notice-close" aria-label="Dismiss" onClick={() => setDeepLinkNotice(null)}>
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── NEWS SECTION ── */}
        {/* ── WHAT'S ON (Stuart, 29 Oct): fixtures, training and events in one
            upcoming list by day, each card labelled. Reading only — adding
            stays on each kind's own tab. Pinned news sits on top as a slim
            strip; a tap opens the post in News. ── */}
        {sectionView === 'whatson' && (() => {
          const pinned = announcements.filter(a => a.is_pinned);
          return (
          <section className="club-section">
            <div className="section-header">
              <h2><CalendarDays size={18} /> What's on</h2>
            </div>

            {pinned.length > 0 && (
              <div className="whatson-pinned" role="group" aria-label="Pinned news">
                {pinned.slice(0, 3).map(ann => (
                  <button key={ann.id} type="button" className="whatson-pinned-row"
                    onClick={() => { selectSection('news'); setDeepLink({ kind: 'news', id: ann.id }); }}>
                    <Pin size={14} aria-hidden="true" className="whatson-pinned-icon" />
                    <span className="whatson-pinned-title">{ann.title}</span>
                    <ChevronRight size={16} aria-hidden="true" className="whatson-pinned-go" />
                  </button>
                ))}
                {pinned.length > 3 && (
                  <button type="button" className="whatson-pinned-more" onClick={() => selectSection('news')}>
                    {pinned.length - 3} more pinned in News
                  </button>
                )}
              </div>
            )}

            {/* Visitors only get club-wide events — say what's missing (F-005). */}
            {!canSeePast && (
              <p className="whatson-members-note">
                Fixtures and training are for {club.name} members.{memberOnlyLink ? '' : ' Ask to join to see them.'}
              </p>
            )}
            {whatsOnError && !whatsOnPage && (
              <div className="empty-state">Couldn't load what's on — please try again.</div>
            )}
            {whatsOnPage && whatsOnEvents.length === 0 && (
              <div className="empty-state">
                {canSeePast
                  ? (selectedTeamFilter !== 'all' ? 'Nothing coming up for this team yet.' : 'Nothing coming up yet.')
                  : 'Nothing open to everyone coming up.'}
              </div>
            )}

            {whatsOnDays.map(day => (
              <div key={day.key} className="whatson-day">
                <h3 className="whatson-day-label">{day.label}</h3>
                {day.items.map(ev => (
                  <EventCard key={ev.id} ev={ev} fixtureView="upcoming" accentColor={accentColor} user={user} showKind
                    openChatEventId={openChatEventId} setOpenChatEventId={setOpenChatEventId}
                    chatMessages={chatMessages} chatOlderCount={chatOlderCount} chatMessage={chatMessage} setChatMessage={setChatMessage}
                    rsvpMutation={rsvpMutation} sendMessageMutation={sendMessageMutation}
                    expandedEventId={expandedEventId} setExpandedEventId={setExpandedEventId} rsvpAttendees={rsvpAttendees} canChaseRsvp={canChaseRsvp}
                    canManage={canManageEvents} onEditEvent={handleEditEvent} onDeleteEvent={handleDeleteEvent} remindMutation={remindMutation}
                    isNextUpcoming={ev.id === nextWhatsOnId} myChildren={myChildren} membership={membership} />
                ))}
              </div>
            ))}
            {whatsOnMeta.hasMore && whatsOnLimit < EVENTS_MAX && (
              <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 8 }}
                onClick={() => setWhatsOnLimit(n => Math.min(n + EVENTS_PAGE, EVENTS_MAX))}>
                Show more ({whatsOnMeta.total - whatsOnEvents.length} more)
              </button>
            )}
            {whatsOnMeta.hasMore && whatsOnLimit >= EVENTS_MAX && (
              <div className="empty-state" style={{ marginTop: 8 }}>
                Showing the next {whatsOnEvents.length} of {whatsOnMeta.total} — use the team filter to see the rest.
              </div>
            )}
          </section>
          );
        })()}

        {sectionView === 'news' && (
          <section className="club-section">
            <div className="section-header">
              <h2><Megaphone size={18} /> News &amp; Announcements</h2>
              {canPost && (
                <div className="section-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => { setJustPosted(null); setShowNewAnn(true); }}>
                    <Plus size={14} /> Post
                  </button>
                </div>
              )}
            </div>

            {showNewAnn && (
              <div className="card new-announcement-form">
                <h3>New Announcement</h3>
                <div className="form-group">
                  <label className="label">Title</label>
                  <input className="input" placeholder="e.g. Training cancelled this week"
                    value={newAnnouncement.title}
                    onChange={e => setNewAnnouncement(p => ({ ...p, title: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Message</label>
                  <textarea className="input" rows={3} placeholder="Write your announcement..."
                    value={newAnnouncement.body}
                    onChange={e => setNewAnnouncement(p => ({ ...p, body: e.target.value }))} />
                </div>
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="label">Category</label>
                    <select className="input" value={newAnnouncement.category}
                      onChange={e => setNewAnnouncement(p => ({ ...p, category: e.target.value }))}>
                      {['general', 'training', 'match', 'event', 'urgent'].map(c => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="label">Team <span className="optional">(optional)</span></label>
                    {/* UC-B2 QA: only teams this person manages (the server
                        refuses others), as the fixture forms. */}
                    <select className="input" value={newAnnouncement.teamId}
                      onChange={e => setNewAnnouncement(p => ({ ...p, teamId: e.target.value }))}>
                      {canClubWide ? (
                        <option value="">Club-wide</option>
                      ) : (
                        <option value="">-- Select a team --</option>
                      )}
                      {manageableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>
                {/* UC-B2: pinned posts stay above newer ones until unpinned. */}
                <label className="ann-pin-toggle">
                  <input type="checkbox" checked={!!newAnnouncement.isPinned}
                    onChange={e => setNewAnnouncement(p => ({ ...p, isPinned: e.target.checked }))} />
                  <span>
                    <strong>Pin to top</strong>
                    <span className="ann-pin-hint">Stays above newer posts until you unpin it.</span>
                  </span>
                </label>
                <p className="form-hint ann-push-hint">
                  {newAnnouncement.teamId
                    ? "This team's members and parents get a phone notification showing the title."
                    : 'Everyone in the club gets a phone notification showing the title.'}
                </p>
                <div className="form-actions" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => addAnnMutation.mutate()} disabled={!newAnnouncement.title || !newAnnouncement.body || (!canClubWide && !newAnnouncement.teamId) || addAnnMutation.isPending}>{addAnnMutation.isPending ? 'Posting…' : 'Post'}</button>
                  <button className="btn btn-ghost" onClick={() => setShowNewAnn(false)}>Cancel</button>
                </div>
              </div>
            )}

            {justPosted && !showNewAnn && (
              <div className="card news-posted-share" role="status">
                <div className="news-posted-text">
                  {justPosted.members_only ? (
                    <>
                      <span><strong>Posted.</strong> Let your WhatsApp groups know it's there.</span>
                      {/* B-62: the message carries no title or text (lib/newsShareText.js). */}
                      <span className="news-posted-note">This post is for club members only, so sharing sends a sign-in link, not the post itself.</span>
                    </>
                  ) : (
                    <span><strong>Posted.</strong> Share it to your WhatsApp groups and social media so nobody misses it.</span>
                  )}
                </div>
                <div className="news-posted-actions">
                  <ShareMenu
                    text={newsShareText(justPosted, club?.name, newsShareUrl(clubSlug, justPosted.id))}
                    clubSlug={clubSlug} kind="news" targetId={justPosted.id}
                    label="Share post" className="btn btn-primary btn-sm" align="right"
                  />
                  <button className="btn btn-ghost btn-icon" aria-label="Dismiss" onClick={() => setJustPosted(null)}>
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            {announcements.length === 0 && !showNewAnn && <div className="empty-state">No announcements yet.</div>}
            {announcements.map(ann => (
              <div key={ann.id} className="announcement-card card" data-ann-id={ann.id}>
                <div className="ann-header">
                  <div className="ann-meta">
                    {ann.is_pinned && (
                      <span className="ann-pinned">
                        <Pin size={13} aria-hidden="true" style={{ color: accentColor }} /> Pinned
                      </span>
                    )}
                    <span className="badge" style={{ background: `${categoryColors[ann.category] || accentColor}18`, color: categoryColors[ann.category] || accentColor }}>
                      {ann.category}
                    </span>
                    {ann.team_name && (
                      <span className="badge" style={{ background: `${accentColor}15`, color: accentColor, fontSize: 12 }}>
                        {ann.team_name}
                      </span>
                    )}
                    <span className="ann-author">{ann.author_name}</span>
                    <span className="ann-date">{format(parseISO(ann.published_at), 'd MMM yyyy')}</span>
                  </div>
                  {/* WT-27: Share / Delete are 40px to tap (Share was 30×26). */}
                  <div className="ann-actions">
                    <ShareMenu
                      text={newsShareText(ann, club?.name, newsShareUrl(clubSlug, ann.id))}
                      clubSlug={clubSlug} kind="news" targetId={ann.id}
                      label="Share" iconOnly={!canPost} className={canPost ? 'btn btn-ghost btn-sm' : 'btn btn-ghost btn-icon'} align="right"
                    />
                    {/* UC-B2: Pin / Unpin — whoever may manage this post. */}
                    {canManagePost(ann) && (
                      <button className="btn btn-ghost btn-icon"
                        title={ann.is_pinned ? 'Unpin' : 'Pin to top'}
                        aria-label={ann.is_pinned ? 'Unpin this post' : 'Pin this post to the top'}
                        aria-pressed={!!ann.is_pinned}
                        disabled={pinAnnMutation.isPending}
                        onClick={() => pinAnnMutation.mutate({ id: ann.id, isPinned: !ann.is_pinned })}>
                        {ann.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
                      </button>
                    )}
                    {/* WT-7: the bin used to delete in one tap, with no undo. Same
                        window.confirm as the event deletes. */}
                    {canManagePost(ann) && (
                      <button className="btn btn-ghost btn-icon" title="Delete" aria-label="Delete this post"
                        onClick={() => {
                          if (window.confirm(`Delete "${ann.title}"? Members won't see this post any more.`)) deleteAnnMutation.mutate(ann.id);
                        }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <h3 className="ann-title">{ann.title}</h3>
                {/* WT-28 (B-52): a link shown as a card isn't printed again above it. */}
                {(() => {
                  const text = bodyWithoutCardLinks(ann.body);
                  return text ? <p className="ann-body">{text}</p> : null;
                })()}
                <SocialLinkCards body={ann.body} />
              </div>
            ))}
            {/* B-24: older news used to be simply unreachable past the first
                20 — pinned notices ate the slots first, so a club with 20
                pins showed no recent posts at all. */}
            {announcementsHasMore && newsLimit < NEWS_MAX && (
              <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 8 }}
                onClick={() => setNewsLimit(n => Math.min(n + NEWS_PAGE, NEWS_MAX))}>
                Show older news ({announcementsTotal - announcements.length} more)
              </button>
            )}
            {announcementsHasMore && newsLimit >= NEWS_MAX && (
              <div className="empty-state" style={{ marginTop: 8 }}>
                Showing {announcements.length} of {announcementsTotal} posts — use the team filter to see the rest.
              </div>
            )}
          </section>
        )}

        {/* ── FIXTURES SECTION (matches only) ── */}
        {sectionView === 'fixtures' && (
          <section className="club-section">
            <div className="section-header">
              <h2><Trophy size={18} /> Fixtures</h2>
              {canManageEvents && (
              <div className="section-actions section-actions-even">
                <button className="btn btn-secondary btn-sm" data-tour="hub-add-fixture" onClick={() => {
                  setNewEvent({ title: '', description: '', eventType: 'match', location: '', startsAt: '', endsAt: '', isHome: null, opponent: '', opponentClubId: null, selectedOpponent: null, venueLat: null, venueLng: null, ...onlyTeamPreset /* WT-21 */, titleDirty: false, locationDirty: false, recurType: 'none', recurUntil: '' });
                  // B-82: Add while an edit is open used to save over the
                  // event being edited. Start a genuinely new one.
                  setEditingEventId(null); setEditScope('occurrence');
                  setShowNewEvent(true);
                }}>
                  <Plus size={14} /> Add
                </button>
                <button className="btn btn-secondary btn-sm" data-tour="hub-bulk-fixtures"
                  onClick={() => setShowBulkFixtures(true)}>
                  <Upload size={14} /> Add many
                </button>
                {/* B-43.1 Fixture Finder — admins, TMs and coaches (server checks too). */}
                <button className="btn btn-secondary btn-sm" onClick={() => setShowFixtureFinder(true)}>
                  <MapPin size={14} /> Find a game
                </button>
              </div>
              )}
            </div>

            {/* Upcoming / Past sub-toggle */}
            {canSeePast && (
            <div className="fixture-view-toggle">
              <button
                className={`fixture-view-btn ${fixtureView === 'upcoming' ? 'active' : ''}`}
                onClick={() => { setFixtureView('upcoming'); setOpenChatEventId(null); }}
              >Upcoming</button>
              <button
                className={`fixture-view-btn ${fixtureView === 'past' ? 'active' : ''}`}
                onClick={() => { setFixtureView('past'); setOpenChatEventId(null); }}
              >Past</button>
            </div>
            )}

            {showNewEvent && (
              <div className="card new-announcement-form" ref={eventFormRef}>
                <h3>{editingEventId ? 'Edit Fixture' : 'Add Fixture'}</h3>
                {/* Title is auto-built from team + opponent + home/away on
                    create (e.g. "U11s Taunton Away"). Only editable afterwards. */}
                {editingEventId ? (
                  <div className="form-group">
                    <label className="label">Title <span className="required">*</span></label>
                    <input className="input" placeholder='e.g. "U11s Taunton Away"'
                      value={newEvent.title}
                      onChange={e => setNewEvent(p => ({ ...p, title: e.target.value, titleDirty: true }))} />
                  </div>
                ) : null}
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="label">Opponent</label>
                    <OpponentTypeahead
                      clubSlug={clubSlug}
                      value={newEvent.opponent}
                      onFreeText={(val) => setNewEvent(p => ({
                        ...p,
                        opponent: val,
                        opponentClubId: null,
                        selectedOpponent: null,
                      }))}
                      onSelect={(opp) => setNewEvent(p => {
                        const next = {
                          ...p,
                          opponent: opp.name,
                          opponentClubId: opp.id,
                          selectedOpponent: opp, // cache so isHome change can re-derive venue
                        };
                        // Auto-fill title (only if user hasn't manually edited)
                        if (!p.titleDirty) {
                          const opponentLabel = opp.short_name || opp.name;
                          if (p.isHome === false) next.title = `${opponentLabel} Away`;
                          else if (p.isHome === true) next.title = `vs ${opponentLabel}`;
                          else if (!p.title) next.title = `vs ${opponentLabel}`;
                        }
                        // Re-derive venue based on the current isHome. This makes
                        // ordering irrelevant: pick opponent first or Home/Away first,
                        // venue still auto-fills once both are set.
                        return applyVenueAutofill(next, club);
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Home / Away</label>
                    <select className="input" value={newEvent.isHome === null ? '' : newEvent.isHome}
                      onChange={e => {
                        const isHome = e.target.value === '' ? null : e.target.value === 'true';
                        setNewEvent(p => {
                          const next = { ...p, isHome };
                          // Title auto-fill on home/away flip
                          if (p.opponent && !p.titleDirty) {
                            const lbl = p.selectedOpponent?.short_name || p.opponent;
                            if (isHome === false) next.title = `${lbl} Away`;
                            else if (isHome === true) next.title = `vs ${lbl}`;
                          }
                          // Venue: re-derive from opp (away) or club home ground (home)
                          return applyVenueAutofill(next, club);
                        });
                      }}>
                      <option value="">-- Select --</option>
                      <option value="true">Home</option>
                      <option value="false">Away</option>
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Team <span className="required">*</span></label>
                  <select className="input" value={newEvent.teamId}
                    onChange={e => {
                      const teamId = e.target.value;
                      const team = teams.find(t => t.id === teamId);
                      setNewEvent(p => ({ ...p, teamId, teamName: team ? team.name : '' }));
                    }}>
                    <option value="">-- Select a team --</option>
                    {manageableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginTop: 12, position: 'relative' }}>
                  <label className="label">Venue</label>

                  {/* If we have coords (auto-filled from opponent or home ground,
                      or pinned via the search below), show the pinned state. */}
                  {newEvent.venueLat && newEvent.venueLng ? (
                    <div style={{
                      padding: '10px 12px',
                      background: 'rgba(30, 74, 53, 0.08)',
                      borderLeft: '3px solid var(--marakin-accent, #1E4A35)',
                      borderRadius: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}>
                      <div>
                        <p style={{ fontSize: 14, color: 'var(--marakin-accent, #1E4A35)', fontWeight: 600, marginBottom: 2 }}>
                          ✓ Venue pinned
                        </p>
                        <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                          {newEvent.location || 'Map will show on the event card'}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={handleClearVenue}
                        style={{ flexShrink: 0 }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* No coords yet. For away fixtures (or any event without
                          coords), show a Mapbox autocomplete. Admin picks the
                          right venue with their eyes on the result. */}
                      <input
                        className="input"
                        placeholder={newEvent.isHome === false
                          ? `Search for ${newEvent.opponent || "the opponent's"} ground — name, postcode, or address`
                          : 'Search venue — name, postcode, or address'}
                        value={venueSearchQuery || newEvent.location}
                        onChange={e => {
                          // Typing here both updates the location text AND fires the autocomplete
                          setNewEvent(p => ({ ...p, location: e.target.value, locationDirty: true }));
                          handleVenueSearch(e.target.value);
                        }}
                        onFocus={() => venueSearchResults.length > 0 && setVenueSearchOpen(true)}
                        onBlur={() => setTimeout(() => setVenueSearchOpen(false), 150)}
                        autoComplete="off"
                      />
                      {venueSearchLoading && (
                        <span style={{ position: 'absolute', right: 14, top: 38, fontSize: 13, color: 'var(--text-muted, #6B7280)' }}>
                          Searching…
                        </span>
                      )}

                      {/* Autocomplete dropdown */}
                      {venueSearchOpen && venueSearchResults.length > 0 && (
                        <div style={{
                          marginTop: 4,
                          background: '#fff',
                          border: '1px solid var(--border, #E5E7EB)',
                          borderRadius: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                          maxHeight: 280,
                          overflowY: 'auto',
                        }}>
                          {venueSearchResults.map((c, i) => {
                            const isPOI = c.kind === 'poi';
                            const isPostcodeArea = c.kind === 'postcode_area';
                            return (
                              <button
                                key={i}
                                type="button"
                                onMouseDown={() => handlePickVenueFromSearch(c)}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  background: 'transparent',
                                  border: 'none',
                                  borderBottom: i < venueSearchResults.length - 1 ? '1px solid #F3F4F6' : 'none',
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  whiteSpace: 'normal',
                                }}
                                onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2, #F4EFE6)'}
                                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                  <span style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    padding: '1px 5px',
                                    borderRadius: 3,
                                    background: isPOI ? 'var(--marakin-accent, #1E4A35)' : (isPostcodeArea ? '#9CA3AF' : '#6B7280'),
                                    color: '#fff',
                                    letterSpacing: 0.5,
                                  }}>
                                    {isPOI ? 'VENUE' : (isPostcodeArea ? 'POSTCODE' : 'ADDRESS')}
                                  </span>
                                  {c.name && c.name !== c.displayName && (
                                    <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                                  )}
                                </div>
                                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>📍 {c.displayName}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
                        Pick the right address from the suggestions so the map pins precisely.
                      </span>
                    </>
                  )}
                </div>
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="label">Kick-off <span className="required">*</span></label>
                    <DateTimeField value={newEvent.startsAt}
                      onChange={v => setNewEvent(p => ({
                        ...p,
                        startsAt: v,
                        endsAt: autoEndFromStart(v, p.eventType),
                        // Auto-fill meet time (30 min before KO) + RSVP deadline
                        // (club default days before) until the user edits them
                        // manually — then their values stick.
                        meetTime: p.meetTimeDirty ? p.meetTime : autoMeetFromStart(v),
                        rsvpBy: p.rsvpByDirty ? p.rsvpBy : autoRsvpByFromStart(v),
                      }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">Ends At <span className="optional">(optional)</span></label>
                    <DateTimeField value={newEvent.endsAt}
                      onChange={v => setNewEvent(p => ({ ...p, endsAt: v }))} />
                  </div>
                </div>
                {/* v31 — meet time + respond-by (tickets #7 + #11) */}
                <div className="form-row-2col" style={{ marginTop: 12 }}>
                  <div className="form-group">
                    <label className="label">Meet time <span className="optional">(optional)</span></label>
                    <TimeSelect value={newEvent.meetTime || ''} disabled={!newEvent.startsAt}
                      onChange={v => setNewEvent(p => ({ ...p, meetTime: v, meetTimeDirty: true }))} />
                    <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
                      {newEvent.startsAt
                        ? 'Auto-set to 30 min before kick-off — change it if you need.'
                        : 'Set a Kick-off first.'}
                    </span>
                  </div>
                  <div className="form-group">
                    <label className="label">Respond by <span className="optional">(optional)</span></label>
                    <input className="input" type="date" value={newEvent.rsvpBy || ''}
                      max={newEvent.startsAt ? newEvent.startsAt.slice(0, 10) : undefined}
                      onChange={e => setNewEvent(p => ({ ...p, rsvpBy: e.target.value, rsvpByDirty: true }))} />
                    <span className="form-hint" style={{ color: 'var(--text-muted, #6B7280)' }}>
                      {respondByHint}
                    </span>
                  </div>
                </div>
                {renderCapacityBlock()}
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Notes <span className="optional">(optional)</span></label>
                  <textarea className="input" rows={2} placeholder="Any extra details..."
                    value={newEvent.description}
                    onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} />
                </div>
                {renderTicketingBlock()}
                <div className="form-actions" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => handleCreateEvent()} disabled={!newEvent.startsAt || !newEvent.teamId || (editingEventId && !newEvent.title) || savingEvent || addEventMutation.isPending}>{(savingEvent || addEventMutation.isPending) ? 'Saving…' : (editingEventId ? 'Save changes' : 'Create')}</button>
                  <button className="btn btn-ghost" onClick={cancelEventForm}>Cancel</button>
                </div>
                {!editingEventId && (!newEvent.startsAt || !newEvent.teamId) && (
                  <span className="form-hint-disabled">
                    {!newEvent.startsAt && !newEvent.teamId
                      ? 'Pick a Team and Kick-off to enable Create. The fixture name is built automatically.'
                      : !newEvent.teamId
                        ? 'Pick a Team to enable Create.'
                        : 'Pick a Kick-off time to enable Create.'}
                  </span>
                )}
              </div>
            )}

            {fixtureEvents.length === 0 && !showNewEvent && (
              <div className="empty-state">
                {!canSeePast ? `Fixtures are for ${club.name} members.${memberOnlyLink ? '' : ' Ask to join to see them.'}` : fixtureView === 'upcoming' ? 'No upcoming fixtures.' : 'No past fixtures found.'}
              </div>
            )}

            {fixtureEvents.filter(ev => !(showNewEvent && editingEventId === ev.id)).map(ev => (
              <EventCard key={ev.id} ev={ev} fixtureView={fixtureView} accentColor={accentColor} user={user}
                openChatEventId={openChatEventId} setOpenChatEventId={setOpenChatEventId}
                chatMessages={chatMessages} chatOlderCount={chatOlderCount} chatMessage={chatMessage} setChatMessage={setChatMessage}
                rsvpMutation={rsvpMutation} sendMessageMutation={sendMessageMutation}
                expandedEventId={expandedEventId} setExpandedEventId={setExpandedEventId} rsvpAttendees={rsvpAttendees} canChaseRsvp={canChaseRsvp}
                canManage={canManageEvents} onEditEvent={handleEditEvent} onDeleteEvent={handleDeleteEvent} remindMutation={remindMutation}
                isNextUpcoming={ev.id === nextFixtureId} myChildren={myChildren} membership={membership} />
            ))}
            {eventsMeta.match.hasMore && eventLimits.match < EVENTS_MAX && (
              <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 8 }}
                onClick={() => showMoreEvents('match')}>
                Show more fixtures ({eventsMeta.match.total - fixtureEvents.length} more)
              </button>
            )}
            {eventsMeta.match.hasMore && eventLimits.match >= EVENTS_MAX && (
              <div className="empty-state" style={{ marginTop: 8 }}>
                Showing {fixtureEvents.length} of {eventsMeta.match.total} fixtures — use the team filter to see the rest.
              </div>
            )}
          </section>
        )}

        {/* ── TRAINING SECTION ── */}
        {sectionView === 'training' && (
          <section className="club-section">
            <div className="section-header">
              <h2><Dumbbell size={18} /> Training</h2>
              {canManageEvents && (
              <div className="section-actions section-actions-even">
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  // Training defaults to the club's home ground when it's set.
                  // 95% of training happens there — saves the user from having
                  // to pin the venue every single time.
                  const homeLat = club?.home_ground_lat ? Number(club.home_ground_lat) : null;
                  const homeLng = club?.home_ground_lng ? Number(club.home_ground_lng) : null;
                  setNewEvent({
                    title: '', description: '', eventType: 'training',
                    location: homeGroundLabel(club), // WT-21
                    startsAt: '', endsAt: '',
                    isHome: null, opponent: '', opponentClubId: null, selectedOpponent: null,
                    venueLat: homeLat, venueLng: homeLng,
                    ...onlyTeamPreset, // WT-21
                    titleDirty: false, locationDirty: false,
                    recurType: 'none', recurUntil: '',
                  });
                  // B-82: Add while an edit is open used to save over the
                  // event being edited. Start a genuinely new one.
                  setEditingEventId(null); setEditScope('occurrence');
                  setShowNewEvent(true);
                }}>
                  <Plus size={14} /> Add
                </button>
                <button className="btn btn-secondary btn-sm" data-tour="hub-season-training"
                  onClick={() => setShowSeasonTraining(true)}>
                  <Dumbbell size={14} /> Set up the season
                </button>
              </div>
              )}
            </div>

            {/* Upcoming / Past sub-toggle */}
            {canSeePast && (
            <div className="fixture-view-toggle">
              <button
                className={`fixture-view-btn ${fixtureView === 'upcoming' ? 'active' : ''}`}
                onClick={() => { setFixtureView('upcoming'); setOpenChatEventId(null); }}
              >Upcoming</button>
              <button
                className={`fixture-view-btn ${fixtureView === 'past' ? 'active' : ''}`}
                onClick={() => { setFixtureView('past'); setOpenChatEventId(null); }}
              >Past</button>
            </div>
            )}

            {showNewEvent && (
              <div className="card new-announcement-form" ref={eventFormRef}>
                <h3>{editingEventId ? 'Edit Training Session' : 'Add Training Session'}</h3>
                {editingEventId && newEvent.seriesId && editScope === 'series' && (
                  <div style={{
                    marginBottom: 12,
                    padding: '8px 12px',
                    background: 'rgba(196, 148, 58, 0.12)',
                    borderLeft: '3px solid #C4943A',
                    borderRadius: 6,
                    fontSize: 14,
                  }}>
                    <strong>Editing this and every later session.</strong> Changes to title, team, venue, and notes apply across the series. Start/end times stay per occurrence — to move the whole series, delete this and future and recreate.
                  </div>
                )}
                {editingEventId && (
                  <div className="form-group">
                    <label className="label">Title</label>
                    <input className="input" placeholder="e.g. U11 Training, Senior Squad Session"
                      value={newEvent.title}
                      onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} />
                  </div>
                )}
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Team <span className="required">*</span></label>
                  <select className="input" value={newEvent.teamId}
                    onChange={e => {
                      const teamId = e.target.value;
                      const team = teams.find(t => t.id === teamId);
                      setNewEvent(p => ({ ...p, teamId, teamName: team ? team.name : '' }));
                    }}>
                    <option value="">-- Select a team --</option>
                    {manageableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                {renderVenuePicker({
                  placeholder: 'Search venue — name, postcode, or address',
                  helperText: 'Defaults to your home ground. Search to pick a different venue.',
                })}
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="label">Starts At</label>
                    <DateTimeField value={newEvent.startsAt}
                      onChange={v => setNewEvent(p => ({ ...p, startsAt: v, endsAt: autoEndFromStart(v, p.eventType), rsvpBy: p.rsvpByDirty ? p.rsvpBy : autoRsvpByFromStart(v) }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">Ends At <span className="optional">(optional)</span></label>
                    <DateTimeField value={newEvent.endsAt}
                      onChange={v => setNewEvent(p => ({ ...p, endsAt: v }))} />
                  </div>
                </div>
                {renderRespondByBlock()}
                {renderCapacityBlock()}
                {renderRecurrenceBlock()}
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Description <span className="optional">(optional)</span></label>
                  <textarea className="input" rows={2} placeholder="Any extra details..."
                    value={newEvent.description}
                    onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="form-actions" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => handleCreateEvent()} disabled={(editingEventId && !newEvent.title) || !newEvent.startsAt || (!editingEventId && !newEvent.teamId) || savingEvent || addEventMutation.isPending || (newEvent.recurType && newEvent.recurType !== 'none' && !newEvent.recurUntil)}>{(savingEvent || addEventMutation.isPending) ? 'Saving…' : (editingEventId ? 'Save changes' : (newEvent.recurType && newEvent.recurType !== 'none' ? 'Create series' : 'Create'))}</button>
                  <button className="btn btn-ghost" onClick={cancelEventForm}>Cancel</button>
                </div>
              </div>
            )}

            {trainingEvents.length === 0 && !showNewEvent && (
              <div className="empty-state">
                {!canSeePast ? `Training is for ${club.name} members.${memberOnlyLink ? '' : ' Ask to join to see it.'}` : fixtureView === 'upcoming' ? 'No upcoming training sessions.' : 'No past training sessions found.'}
              </div>
            )}

            {trainingEvents.filter(ev => !(showNewEvent && editingEventId === ev.id)).map(ev => (
              <EventCard key={ev.id} ev={ev} fixtureView={fixtureView} accentColor={accentColor} user={user}
                openChatEventId={openChatEventId} setOpenChatEventId={setOpenChatEventId}
                chatMessages={chatMessages} chatOlderCount={chatOlderCount} chatMessage={chatMessage} setChatMessage={setChatMessage}
                rsvpMutation={rsvpMutation} sendMessageMutation={sendMessageMutation}
                expandedEventId={expandedEventId} setExpandedEventId={setExpandedEventId} rsvpAttendees={rsvpAttendees} canChaseRsvp={canChaseRsvp}
                canManage={canManageEvents} onEditEvent={handleEditEvent} onDeleteEvent={handleDeleteEvent} remindMutation={remindMutation}
                isNextUpcoming={ev.id === nextTrainingId} myChildren={myChildren} membership={membership} />
            ))}
            {eventsMeta.training.hasMore && eventLimits.training < EVENTS_MAX && (
              <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 8 }}
                onClick={() => showMoreEvents('training')}>
                Show more training sessions ({eventsMeta.training.total - trainingEvents.length} more)
              </button>
            )}
            {eventsMeta.training.hasMore && eventLimits.training >= EVENTS_MAX && (
              <div className="empty-state" style={{ marginTop: 8 }}>
                Showing {trainingEvents.length} of {eventsMeta.training.total} training sessions — use the team filter to see the rest.
              </div>
            )}
          </section>
        )}

        {/* ── EVENTS SECTION (social, general) ── */}
        {sectionView === 'events' && (
          <section className="club-section">
            <div className="section-header">
              <h2><Calendar size={18} /> Events</h2>
              {canManageEvents && (
              <div className="section-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  // Social events most commonly happen at the clubhouse, so
                  // mirror training and default to the home ground when known.
                  const homeLat = club?.home_ground_lat ? Number(club.home_ground_lat) : null;
                  const homeLng = club?.home_ground_lng ? Number(club.home_ground_lng) : null;
                  setNewEvent({
                    title: '', description: '', eventType: 'social',
                    location: homeGroundLabel(club), // WT-21
                    startsAt: '', endsAt: '',
                    isHome: null, opponent: '', opponentClubId: null, selectedOpponent: null,
                    venueLat: homeLat, venueLng: homeLng,
                    // WT-21: Team is optional here; only someone who can't post
                    // club-wide (so must pick a team) gets their one team ready.
                    ...(canClubWide ? { teamName: '', teamId: '' } : onlyTeamPreset),
                    titleDirty: false, locationDirty: false,
                    recurType: 'none', recurUntil: '',
                  });
                  // B-82: Add while an edit is open used to save over the
                  // event being edited. Start a genuinely new one.
                  setEditingEventId(null); setEditScope('occurrence');
                  setShowNewEvent(true);
                }}>
                  <Plus size={14} /> Add
                </button>
              </div>
              )}
            </div>

            {/* Upcoming / Past sub-toggle */}
            {canSeePast && (
            <div className="fixture-view-toggle">
              <button
                className={`fixture-view-btn ${fixtureView === 'upcoming' ? 'active' : ''}`}
                onClick={() => { setFixtureView('upcoming'); setOpenChatEventId(null); }}
              >Upcoming</button>
              <button
                className={`fixture-view-btn ${fixtureView === 'past' ? 'active' : ''}`}
                onClick={() => { setFixtureView('past'); setOpenChatEventId(null); }}
              >Past</button>
            </div>
            )}

            {showNewEvent && (
              <div className="card new-announcement-form" ref={eventFormRef}>
                <h3>{editingEventId ? 'Edit Event' : 'Add Event'}</h3>
                {editingEventId && newEvent.seriesId && editScope === 'series' && (
                  <div style={{
                    marginBottom: 12,
                    padding: '8px 12px',
                    background: 'rgba(196, 148, 58, 0.12)',
                    borderLeft: '3px solid #C4943A',
                    borderRadius: 6,
                    fontSize: 14,
                  }}>
                    <strong>Editing this and every later event.</strong> Changes to title, team, venue, and notes apply across the series. Start/end times stay per occurrence — to move the whole series, delete this and future and recreate.
                  </div>
                )}
                <div className="form-group">
                  <label className="label">Title</label>
                  <input className="input" placeholder="e.g. U11s Training, Beer &amp; Cider Festival"
                    value={newEvent.title}
                    onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} />
                </div>
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="label">Type</label>
                    <select className="input" value={newEvent.eventType}
                      onChange={e => setNewEvent(p => ({ ...p, eventType: e.target.value }))}>
                      <option value="social">Social</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="label">Team <span className="optional">(optional)</span></label>
                    <select className="input" value={newEvent.teamId}
                      onChange={e => {
                        const teamId = e.target.value;
                        const team = teams.find(t => t.id === teamId);
                        setNewEvent(p => ({ ...p, teamId, teamName: team ? team.name : '' }));
                      }}>
                      {canClubWide ? (
                        <option value="">Club-wide</option>
                      ) : (
                        <option value="">-- Select a team --</option>
                      )}
                      {manageableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>
                {renderVenuePicker({
                  placeholder: 'Search venue — name, postcode, or address',
                  helperText: 'Defaults to your home ground. Search to pick a different venue.',
                })}
                <div className="form-row-2col">
                  <div className="form-group">
                    <label className="label">Starts At</label>
                    <DateTimeField value={newEvent.startsAt}
                      onChange={v => setNewEvent(p => ({ ...p, startsAt: v, endsAt: autoEndFromStart(v, p.eventType), rsvpBy: p.rsvpByDirty ? p.rsvpBy : autoRsvpByFromStart(v) }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">Ends At <span className="optional">(optional)</span></label>
                    <DateTimeField value={newEvent.endsAt}
                      onChange={v => setNewEvent(p => ({ ...p, endsAt: v }))} />
                  </div>
                </div>
                {renderRespondByBlock()}
                {renderCapacityBlock()}
                {renderRecurrenceBlock()}
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Description <span className="optional">(optional)</span></label>
                  <textarea className="input" rows={2} placeholder="Any extra details..."
                    value={newEvent.description}
                    onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))} />
                </div>
                {renderTicketingBlock()}
                <div className="form-actions" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => handleCreateEvent()} disabled={!newEvent.title || !newEvent.startsAt || savingEvent || addEventMutation.isPending || (newEvent.recurType && newEvent.recurType !== 'none' && !newEvent.recurUntil)}>{(savingEvent || addEventMutation.isPending) ? 'Saving…' : (editingEventId ? 'Save changes' : (newEvent.recurType && newEvent.recurType !== 'none' ? 'Create series' : 'Create'))}</button>
                  <button className="btn btn-ghost" onClick={cancelEventForm}>Cancel</button>
                </div>
              </div>
            )}

            {otherEvents.length === 0 && !showNewEvent && (
              <div className="empty-state">
                {fixtureView === 'upcoming' ? 'No upcoming events.' : 'No past events found.'}
              </div>
            )}

            {otherEvents.filter(ev => !(showNewEvent && editingEventId === ev.id)).map(ev => (
              <EventCard key={ev.id} ev={ev} fixtureView={fixtureView} accentColor={accentColor} user={user}
                openChatEventId={openChatEventId} setOpenChatEventId={setOpenChatEventId}
                chatMessages={chatMessages} chatOlderCount={chatOlderCount} chatMessage={chatMessage} setChatMessage={setChatMessage}
                rsvpMutation={rsvpMutation} sendMessageMutation={sendMessageMutation}
                expandedEventId={expandedEventId} setExpandedEventId={setExpandedEventId} rsvpAttendees={rsvpAttendees} canChaseRsvp={canChaseRsvp}
                canManage={canManageEvents} onEditEvent={handleEditEvent} onDeleteEvent={handleDeleteEvent} remindMutation={remindMutation}
                isNextUpcoming={ev.id === nextOtherId} myChildren={myChildren} membership={membership} />
            ))}
            {eventsMeta.other.hasMore && eventLimits.other < EVENTS_MAX && (
              <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 8 }}
                onClick={() => showMoreEvents('other')}>
                Show more events ({eventsMeta.other.total - otherEvents.length} more)
              </button>
            )}
            {eventsMeta.other.hasMore && eventLimits.other >= EVENTS_MAX && (
              <div className="empty-state" style={{ marginTop: 8 }}>
                Showing {otherEvents.length} of {eventsMeta.other.total} events — use the team filter to see the rest.
              </div>
            )}
          </section>
        )}
      </div>


      {/* Venue candidate picker — appears mid-save when Nominatim returns
          2-3 possible addresses for the event location. */}
      {venueCandidates.length > 0 && (
        <div className="modal-overlay" onClick={handleSkipVenueCandidate}>
          <div className="modal-card modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Which address is it?</h3>
            </div>
            <p className="modal-subtitle">
              We found a few possible matches for "{pendingEventPayload?.location}". Pick the
              right one so we can show a map and weather forecast.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {venueCandidates.map((c, i) => (
                <button
                  key={i}
                  className="btn btn-ghost"
                  style={{ textAlign: 'left', whiteSpace: 'normal', padding: '10px 12px' }}
                  onClick={() => handlePickVenueCandidate(c)}
                >
                  📍 {c.displayName}
                </button>
              ))}
            </div>
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={handleSkipVenueCandidate}>
                None of these — save without map
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk fixtures wizard (slice 3) — grid or CSV/XLSX upload. */}
      {showBulkFixtures && (
        <BulkAddFixtures
          clubSlug={clubSlug}
          teams={manageableTeams}
          accentColor={accentColor}
          onClose={(didSend) => {
            setShowBulkFixtures(false);
            if (didSend) { qc.invalidateQueries({ queryKey: ['events', clubSlug] }); qc.invalidateQueries({ queryKey: ['setup-status', clubSlug] }); /* F2-1 */ }
          }}
        />
      )}

      {/* B-43.1 Fixture Finder — "Clubs near you" (read-only). */}
      {showFixtureFinder && (
        <FixtureFinder
          clubSlug={clubSlug}
          clubName={club?.name}
          sport={club?.sport}
          isClubAdmin={!!clubAdmin}
          onClose={() => setShowFixtureFinder(false)}
        />
      )}

      {/* Season training wizard (slice 3) — weekly series via v13 recurrence. */}
      {showSeasonTraining && (
        <SeasonTraining
          clubSlug={clubSlug}
          teams={manageableTeams}
          club={club}
          accentColor={accentColor}
          onClose={(didSend) => {
            setShowSeasonTraining(false);
            if (didSend) { qc.invalidateQueries({ queryKey: ['events', clubSlug] }); qc.invalidateQueries({ queryKey: ['setup-status', clubSlug] }); /* F2-1 */ }
          }}
        />
      )}

      {/* Join Modal */}
      {showJoin && (
        <div className="modal-overlay" onClick={closeJoin}>
          <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="join-modal-title" onClick={e => e.stopPropagation()}>
            {/* WT-28: a visible ✕ (and Esc) — the only way out was a tap outside. */}
            <div className="modal-title-row">
              <h2 id="join-modal-title">Join {club.name}</h2>
              <button type="button" className="modal-close" aria-label="Close" onClick={closeJoin}><X size={18} /></button>
            </div>
            {user && joinMutation.isSuccess ? (<>
              <p>
                {joinMutation.data?.data?.status === 'pending'
                  ? `Request sent to ${club.name}. We'll let you know when an admin approves it.`
                  : joinMutation.data?.data?.message}
              </p>
              <div className="form-actions" style={{ marginTop: 20 }}>
                <button className="btn btn-primary" onClick={closeJoin}>Done</button>
              </div>
            </>) : !user ? (<>
              {/* WT-5 (16 Oct walk-through): a signed-out visitor had "Select
                  your role…" with nothing to select and one link that forgot
                  the club. Sign in / Create account both carry this page as
                  the return path (UC-B5's state.from; Register keeps it
                  through the confirm-email link), so they land back here to
                  ask to join. */}
              <p>Sign in to ask to join {club.name}. New to Marakin? Create a free account first. Either way, we'll bring you back here.</p>
              <div className="form-actions join-signin-actions" style={{ marginTop: 20 }}>
                <Link className="btn btn-primary" to="/login" state={{ from: `/${clubSlug}` }}><LogIn size={15} /> Sign in</Link>
                <Link className="btn btn-secondary" to="/register" state={{ from: `/${clubSlug}` }}><UserPlus size={15} /> Create account</Link>
                <button className="btn btn-ghost" onClick={closeJoin}>Cancel</button>
              </div>
            </>) : waitingRequest ? (<>
              {/* WT-4: already waiting (e.g. via a members-only link) → say
                  so here too, with Withdraw, instead of a second request. */}
              <p>Your request is waiting for an admin. {joinRequestSummary(waitingRequest)} We'll let you know when it's approved.</p>
              {withdrawError && (
                <p className="modal-note" role="alert" style={{ marginTop: 12, color: 'var(--danger)' }}>{withdrawError}</p>
              )}
              <div className="form-actions" style={{ marginTop: 20 }}>
                <button className="btn btn-primary" onClick={closeJoin}>Done</button>
                <button className="btn btn-ghost" disabled={withdrawMutation.isPending} onClick={withdrawJoinRequest}>
                  {withdrawMutation.isPending ? 'Withdrawing…' : 'Withdraw request'}
                </button>
              </div>
            </>) : (<>
            <p>Select your role at the club to request membership.</p>
            {user && (<>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="label">Your role</label>
                {/* No Coach (B-58): coaches see members' contact details, so
                    that role comes from an invite or an admin after joining. */}
                <select className="input" value={joinForm.role} onChange={e => setJoinForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="player">Player</option>
                  <option value="parent">Parent / Guardian</option>
                  <option value="supporter">Supporter</option>
                </select>
                <p className="modal-note" style={{ marginTop: 6 }}>Coaching? Ask to join as a Player or Supporter, and a club admin can change your role.</p>
              </div>
              {teams.length > 0 && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="label">Team <span className="optional">(optional)</span></label>
                  <select className="input" value={joinForm.teamName} onChange={e => setJoinForm(p => ({ ...p, teamName: e.target.value }))}>
                    <option value="">-- Select a team --</option>
                    {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
              )}
              {joinMutation.isError && (
                <p className="modal-note" role="alert" style={{ marginTop: 12, color: 'var(--danger)' }}>
                  {joinMutation.error?.response?.data?.error || 'Something went wrong. Please try again.'}
                </p>
              )}
              <div className="form-actions" style={{ marginTop: 20 }}>
                <button className="btn btn-primary" disabled={joinMutation.isPending} onClick={() => joinMutation.mutate()}>
                  {joinMutation.isPending ? 'Sending…' : 'Request to join'}
                </button>
                <button className="btn btn-ghost" onClick={closeJoin}>Cancel</button>
              </div>
            </>)}
            </>)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubHub;
